// ─── Estudio B0C (BREEAM ES ENE 4) ─────────────────────────────────────────
// Módulo interno HAUS para el estudio de viabilidad de tecnologías
// Bajas o Cero Carbono. Cumple el criterio ENE 4 (Manual BREEAM ES NC 2015).
//
// El módulo es un wizard de 4 pantallas:
//   1. Datos del proyecto + fase + emplazamiento (auto vía PVGIS/IDAE/IGME)
//   2. Selección de tecnologías del catálogo maestro
//   3. Comparativa objetiva · aplicable/no + tablas CCV/ACV + rankings por cifras
//   4. Firma externa (asesor ≠ firmante) · docx firmable fuera de la app
//
// Consume las funciones RPC del módulo B0C que ya están en Supabase:
//   · boc_get_idae_factor · boc_get_geothermal · boc_zona_idae
//   · boc_calcular_ccv · boc_ccv_por_estudio · boc_ccv_sensibilidad
//   · boc_poblar_shadow_lca
//
// Y el edge function `pvgis-proxy` para la producción solar.

import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  ArrowLeft, ArrowRight, CheckCircle, Warning, Info,
  Sun, Lightning, Leaf, House,
  FloppyDisk, Signature, Calculator, Spinner,
  PencilSimple, ListChecks, Files, Fire,
} from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'

// ─── Constantes de estilo (paleta HAUS · tierra + oliva) ────────────────────
const C = {
  bg:     '#F5F0EA',
  card:   '#FFFFFF',
  border: '#DDD5C8',
  dark:   '#3B2F2F',
  brown:  '#5C4A3A',
  muted:  '#7A6A5A',
  olive:  '#8D9A2E',
  coral:  '#D4614A',
  amber:  '#C98A2E',
  cream:  '#FAF7F3',
  ok:     '#5B7A2E',
}
// Estilos alineados con CONT1Calculator, MAT1Calculator y AG1Calculator
const CARD = {
  background: C.card, border: `1px solid ${C.border}`, borderRadius: '8px',
  padding: '1.25rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)',
}
const INP = {
  padding: '0.35rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '4px',
  fontSize: '0.8rem', fontFamily: "'DM Sans', sans-serif", color: C.dark,
  background: C.cream, outline: 'none', width: '100%', boxSizing: 'border-box',
}
const INP_RO = { ...INP, background: '#EEEAE4', color: C.muted, cursor: 'default' }
const BTN_P = {
  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
  padding: '0.5rem 1rem', background: C.olive, color: '#fff',
  border: 'none', borderRadius: '5px', cursor: 'pointer',
  fontSize: '0.8rem', fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
}
const BTN_G = {
  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
  padding: '0.35rem 0.75rem', background: 'transparent', color: C.muted,
  border: `1px solid ${C.border}`, borderRadius: '5px', cursor: 'pointer',
  fontSize: '0.78rem', fontFamily: "'DM Sans', sans-serif",
}
const LABEL = { fontSize: '0.68rem', color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '0.2rem', display: 'block' }
const SECTION_H = { fontSize: '1.05rem', color: C.dark, fontWeight: 700, marginBottom: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }

// ─── Fases del proyecto ──────────────────────────────────────────────────────
const FASES = [
  { key: 'viabilidad',        label: 'Estudio de viabilidad', ok: true  },
  { key: 'anteproyecto',      label: 'Anteproyecto',          ok: true  },
  { key: 'proyecto_basico',   label: 'Proyecto básico',       ok: false },
  { key: 'proyecto_ejecucion',label: 'Proyecto de ejecución', ok: false },
]

const ICONO_GRUPO = {
  renovable_electrica: Lightning,
  renovable_termica:   Sun,
  hibrida_red:         Fire,
}

// ─── Componente principal ────────────────────────────────────────────────────
// Estudio default en memoria · renderizamos con esto de inmediato y en paralelo
// vamos rellenando con datos reales de Supabase (patrón CONT1Calculator).
const STUDY_DEFAULT = {
  id: null,
  fase_proyecto: 'viabilidad',
  horizonte_ccv_anos: 60,
  tasa_descuento_pct: 3.0,
  precio_electricidad_eur_kwh: 0.15,
  precio_gas_referencia_eur_kwh: 0.08,
  rendimiento_sistema_ref_pct: 92,
  estado: 'borrador',
  notas_asesor: '',
  firmante_nombre: '',
  firmante_titulacion: '',
  firmante_colegiado: '',
  firmante_email: '',
  fecha_firma_estudio: null,
  tecnologia_recomendada_id: null,
  conclusion_texto: '',
  storage_path_estudio_firmado: null,
}

export default function BOCStudy({ projectId, project }) {
  const [step, setStep] = useState(1)
  const [study, setStudy] = useState(STUDY_DEFAULT)   // renderiza al momento
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [catalog, setCatalog] = useState([])
  const [selectedTechs, setSelectedTechs] = useState({})   // { tech_id: { potencia, coste, produccion, id_studyTech } }
  const [siteData, setSiteData] = useState(null)
  const [ccvResults, setCcvResults] = useState([])
  const [acvResults, setAcvResults] = useState([])

  const flash = useCallback((msg, kind = 'info') => {
    setMessage({ msg, kind })
    setTimeout(() => setMessage(null), 4000)
  }, [])

  // ─── Carga inicial · usuario + catálogo + estudio existente ──────────────
  useEffect(() => {
    let cancelled = false
    async function bootstrap() {
      // NO setLoading(true) · la UI ya está renderizada con defaults
      try {
        // Fase 1 · lo esencial en paralelo
        const [userRes, catRes, existingRes] = await Promise.all([
          supabase.auth.getUser(),
          supabase.from('boc_technology_catalog').select('*').eq('is_active', true).order('orden'),
          supabase.from('boc_studies').select('*')
            .eq('project_id', projectId)
            .in('estado', ['borrador', 'en_revision'])
            .order('created_at', { ascending: false })
            .limit(1).maybeSingle(),
        ])

        const user = userRes.data?.user
        if (!user) throw new Error('Sesión no válida')
        if (cancelled) return
        setCatalog(catRes.data || [])

        // Crear estudio si no existe
        let existing = existingRes.data
        if (!existing) {
          const { data: created, error } = await supabase
            .from('boc_studies')
            .insert({ project_id: projectId, user_id: user.id, estado: 'borrador' })
            .select().single()
          if (error) throw error
          existing = created
        }
        if (cancelled) return
        setStudy(prev => ({ ...prev, ...existing }))   // sobrescribe defaults con datos reales

        // Fase 2 · datos del estudio en paralelo
        // Añadimos las llamadas al motor CCV y a la tabla del ACV en la sombra
        // para que los resultados se muestren INMEDIATAMENTE al abrir Pantalla 3
        // (sin obligar al usuario a pulsar Recalcular cada vez que entra)
        const [techRes, siteRes, ccvRes, acvRes] = await Promise.all([
          supabase.from('boc_study_technologies').select('*').eq('study_id', existing.id),
          supabase.from('boc_site_data').select('*').eq('project_id', projectId).maybeSingle(),
          supabase.rpc('boc_ccv_por_estudio', { p_study_id: existing.id }),
          supabase.from('boc_shadow_lca').select('*').eq('study_id', existing.id),
        ])
        if (cancelled) return

        const t = {}
        for (const row of techRes.data || []) {
          t[row.technology_id] = {
            id: row.id,
            potencia: row.potencia_propuesta_kw,
            coste: row.coste_estimado_eur,
            produccion: row.produccion_esperada_kwh_ano,
            consumo: row.consumo_electrico_kwh_ano,
            areaBruta: row.area_bruta_m2,
            esAplicable: row.es_aplicable,
            motivoNoAplicable: row.motivo_no_aplicable,
            gwpEpdEspecifico: row.gwp_epd_especifico_kg_kw,
            epdUrlProyecto: row.epd_url_proyecto,
            epdDescripcionProyecto: row.epd_descripcion_proyecto,
            notas: row.notas,
          }
        }
        setSelectedTechs(t)
        setSiteData(siteRes.data)
        if (ccvRes.data) setCcvResults(ccvRes.data)
        if (acvRes.data) setAcvResults(acvRes.data)
      } catch (e) {
        flash(`Error cargando el estudio: ${e.message}`, 'err')
      }
    }
    if (projectId) bootstrap()
    return () => { cancelled = true }
  }, [projectId, flash])

  // ─── Guardar estudio (fase, notas, tasa, horizonte) ───────────────────────
  const saveStudy = useCallback(async (patch = {}) => {
    // Actualiza estado local siempre (para responsive UI)
    setStudy(prev => ({ ...prev, ...patch }))
    // Solo persiste a Supabase si el estudio ya está creado (tiene id)
    if (!study.id) return
    setSaving(true)
    const { error } = await supabase
      .from('boc_studies')
      .update(patch)
      .eq('id', study.id)
    setSaving(false)
    if (error) flash(`Error al guardar: ${error.message}`, 'err')
  }, [study.id, flash])

  const gotoStep = (n) => {
    setStep(Math.max(1, Math.min(4, n)))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // ─── Render principal · siempre visible, datos se rellenan sin bloquear ────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Barra de progreso wizard */}
      <StepBar step={step} onGo={gotoStep} />

      {/* Mensaje flotante */}
      {message && (
        <div style={{
          padding: '0.7rem 1rem',
          background: message.kind === 'err' ? '#FDF5F3' : message.kind === 'ok' ? '#F1F5E8' : '#FAF7F3',
          border: `1px solid ${message.kind === 'err' ? C.coral : message.kind === 'ok' ? C.ok : C.border}`,
          borderRadius: '5px', color: C.dark, fontSize: '0.85rem',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          {message.kind === 'err' ? <Warning size={18} weight="regular" /> : <Info size={18} weight="regular" />}
          {message.msg}
        </div>
      )}

      {/* Pantalla activa */}
      {step === 1 && (
        <Step1Info
          study={study} project={project} projectId={projectId}
          siteData={siteData} setSiteData={setSiteData}
          onSave={saveStudy} onNext={() => gotoStep(2)}
          flash={flash}
        />
      )}
      {step === 2 && (
        <Step2Technologies
          catalog={catalog}
          study={study}
          selectedTechs={selectedTechs} setSelectedTechs={setSelectedTechs}
          onBack={() => gotoStep(1)} onNext={() => gotoStep(3)}
          flash={flash}
        />
      )}
      {step === 3 && (
        <Step3Comparison
          catalog={catalog} study={study} siteData={siteData}
          selectedTechs={selectedTechs} setSelectedTechs={setSelectedTechs}
          ccvResults={ccvResults} setCcvResults={setCcvResults}
          acvResults={acvResults} setAcvResults={setAcvResults}
          onBack={() => gotoStep(2)} onNext={() => gotoStep(4)}
          flash={flash}
        />
      )}
      {step === 4 && (
        <Step4Sign
          study={study} catalog={catalog} selectedTechs={selectedTechs}
          onSave={saveStudy}
          onBack={() => gotoStep(3)}
          flash={flash}
        />
      )}

      {saving && (
        <div style={{ position: 'fixed', bottom: '1rem', right: '1rem',
                      background: C.dark, color: '#fff', padding: '0.5rem 0.9rem',
                      borderRadius: '5px', fontSize: '0.8rem',
                      display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <Spinner size={14} weight="regular" /> Guardando…
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
//                      SUB-COMPONENTE · Barra de progreso
// ────────────────────────────────────────────────────────────────────────────
function StepBar({ step, onGo }) {
  const items = [
    { n: 1, label: 'Emplazamiento' },
    { n: 2, label: 'Tecnologías' },
    { n: 3, label: 'Comparativa' },
    { n: 4, label: 'Firma' },
  ]
  return (
    <div style={{ display: 'flex', gap: '0.25rem', background: C.card, padding: '0.35rem',
                  border: `1px solid ${C.border}`, borderRadius: '6px' }}>
      {items.map(it => {
        const active = step === it.n
        const done = step > it.n
        return (
          <button key={it.n}
            onClick={() => onGo(it.n)}
            style={{
              flex: 1, textAlign: 'center',
              padding: '0.4rem 0.6rem',
              background: active ? C.olive : 'transparent',
              color: active ? '#fff' : done ? C.ok : C.muted,
              border: 'none',
              borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem',
              fontFamily: "'DM Sans', sans-serif", fontWeight: active ? 700 : 500,
            }}>
            {it.n}. {it.label}
          </button>
        )
      })}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
//                    PANTALLA 1 · Info + Emplazamiento
// ────────────────────────────────────────────────────────────────────────────
function Step1Info({ study, project, projectId, siteData, setSiteData, onSave, onNext, flash }) {
  const [fase, setFase] = useState(study.fase_proyecto || 'viabilidad')
  const [horizonte, setHorizonte] = useState(study.horizonte_ccv_anos || 60)
  const [tasa, setTasa] = useState(study.tasa_descuento_pct || 3.0)
  const [precioElec, setPrecioElec] = useState(study.precio_electricidad_eur_kwh || 0.15)
  const [precioGas, setPrecioGas] = useState(study.precio_gas_referencia_eur_kwh || 0.08)
  const [rendRef, setRendRef]       = useState(study.rendimiento_sistema_ref_pct || 92)
  const [notas, setNotas] = useState(study.notas_asesor || '')
  const [loadingPvgis, setLoadingPvgis] = useState(false)

  const faseInfo = FASES.find(f => f.key === fase)
  const lat = project?.latitude
  const lon = project?.longitude
  const cpro = project?.ine_code ? project.ine_code.slice(0, 2) : null

  // Cargar PVGIS + IDAE + geotermia si hay coordenadas Y no están ya cacheados
  useEffect(() => {
    async function loadSite() {
      if (!lat || !lon) return

      // Si ya está TODO cacheado (PVGIS + factor de emisión) · no re-consultar
      if (siteData?.radiacion_pvgis
          && siteData?.factor_emision_electricidad_kg_kwh) return

      // Auto-fill del factor IDAE cuando solo falta él · proyectos creados
      // antes de que existiera esta lógica pueden tener PVGIS pero no factor.
      // En vez de re-hacer todo el bootstrap, solo hacemos un PATCH rápido.
      if (siteData?.radiacion_pvgis
          && !siteData?.factor_emision_electricidad_kg_kwh
          && cpro) {
        try {
          const { data: zonaRes } = await supabase.rpc('boc_zona_idae',
            { p_cpro: cpro })
          const zona = zonaRes || 'peninsular'
          const { data: factorRes } = await supabase.rpc('boc_get_idae_factor',
            { p_vector: 'electricidad', p_zona: zona })
          if (factorRes?.[0]) {
            const patch = {
              factor_emision_electricidad_kg_kwh:
                factorRes[0].factor_emision_co2,
              factor_emision_documento: 'IDAE 20/06/2016',
              factor_emision_zona: zona,
              factor_emision_fecha_ref: '2016-06-20',
            }
            await supabase.from('boc_site_data').update(patch)
              .eq('project_id', projectId)
            setSiteData({ ...siteData, ...patch })
          }
        } catch (e) {
          console.warn('Auto-fill factor IDAE falló:', e.message)
        }
        return
      }

      setLoadingPvgis(true)
      try {
        // PVGIS
        const { data: pvgis } = await supabase.functions.invoke('pvgis-proxy', {
          body: { lat: Number(lat), lon: Number(lon), peakpower: 1 },
        })

        // Zona IDAE + factor emisión electricidad
        let zona = 'peninsular', factor = null
        if (cpro) {
          const { data: zonaRes } = await supabase.rpc('boc_zona_idae', { p_cpro: cpro })
          zona = zonaRes || 'peninsular'
          const { data: factorRes } = await supabase.rpc('boc_get_idae_factor', {
            p_vector: 'electricidad', p_zona: zona,
          })
          if (factorRes?.[0]) {
            factor = factorRes[0].factor_emision_co2
          }
        }

        // Potencial geotérmico
        let geo = null
        if (cpro) {
          const { data: geoRes } = await supabase.rpc('boc_get_geothermal', {
            p_cpro: cpro, p_ine: null,
          })
          if (geoRes?.[0]) geo = geoRes[0]
        }

        // Guardar en boc_site_data (upsert)
        const payload = {
          project_id: projectId,
          radiacion_pvgis: pvgis || null,
          radiacion_pvgis_fecha_consulta: pvgis ? new Date().toISOString() : null,
          potencial_geotermico_igme: geo || null,
          potencial_geotermico_fecha: geo ? new Date().toISOString() : null,
          factor_emision_electricidad_kg_kwh: factor,
          factor_emision_documento: 'IDAE 20/06/2016',
          factor_emision_zona: zona,
          factor_emision_fecha_ref: '2016-06-20',
        }
        await supabase.from('boc_site_data').upsert(payload, { onConflict: 'project_id' })
        setSiteData(payload)
      } catch (e) {
        flash(`No se pudo cargar datos del emplazamiento: ${e.message}`, 'err')
      } finally {
        setLoadingPvgis(false)
      }
    }
    loadSite()
  }, [lat, lon, cpro, projectId, siteData, setSiteData, flash])

  const guardar = async () => {
    await onSave({
      fase_proyecto: fase,
      horizonte_ccv_anos: horizonte,
      tasa_descuento_pct: tasa,
      precio_electricidad_eur_kwh: precioElec,
      precio_gas_referencia_eur_kwh: precioGas,
      rendimiento_sistema_ref_pct: rendRef,
      notas_asesor: notas || null,
    })
    flash('Guardado', 'ok')
  }

  const continuar = async () => {
    await guardar()
    onNext()
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Bloque unificado · proyecto + fase + emplazamiento sobre fondo beige */}
      <div style={{ ...CARD, background: C.cream }}>
        <h3 style={SECTION_H}>
          <House size={20} weight="regular" style={{ color: C.olive }} />
          Proyecto, fase y emplazamiento
          {loadingPvgis && <Spinner size={14} weight="regular" style={{ color: C.muted, marginLeft: 'auto' }} />}
        </h3>

        {/* Sub-bloque proyecto */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.6rem 0.8rem', marginBottom: '0.9rem' }}>
          <FieldCompact label="Proyecto" value={project?.name || '—'} />
          <FieldCompact label="Municipio" value={project?.municipality || '—'} />
          <FieldCompact label="Provincia" value={project?.province || '—'} />
          <FieldCompact label="Coordenadas" value={lat && lon ? `${lat}, ${lon}` : 'sin coordenadas'} />
        </div>

        {/* Sub-bloque fase (dropdown compacto en fila) */}
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '0.6rem 0.8rem', alignItems: 'center', marginBottom: '0.9rem' }}>
          <label style={{ ...LABEL, marginBottom: 0 }}>Fase del proyecto</label>
          <select value={fase} onChange={e => setFase(e.target.value)}
            style={{ ...INP, maxWidth: '280px' }}>
            {FASES.map(f => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </select>
        </div>
        {faseInfo && !faseInfo.ok && (
          <div style={{ padding: '0.5rem 0.8rem', background: '#FDF5F3', border: `1px solid ${C.coral}`,
                        borderRadius: '4px', color: C.dark, fontSize: '0.76rem',
                        display: 'flex', gap: '0.4rem', alignItems: 'flex-start', marginBottom: '0.9rem' }}>
            <Warning size={14} weight="regular" style={{ color: C.coral, flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong>{faseInfo.label}</strong> · BREEAM puede rechazar el punto ENE 4 porque el estudio
              debería hacerse en viabilidad/anteproyecto. Deja registro en las notas de por qué se hace ahora.
            </div>
          </div>
        )}

        {/* Separador */}
        <div style={{ height: '1px', background: C.border, margin: '0.4rem 0 0.9rem' }} />

        {/* Sub-bloque datos del emplazamiento */}
        {!lat || !lon ? (
          <div style={{ padding: '0.5rem 0.8rem', background: '#FAF7F3', border: `1px solid ${C.amber}`, borderRadius: '4px', fontSize: '0.76rem' }}>
            <Warning size={14} weight="regular" style={{ color: C.amber, verticalAlign: 'middle', marginRight: '0.3rem' }} />
            Sin coordenadas · añade lat/lon en Datos del proyecto para cargar PVGIS, IDAE e IGME automáticamente.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.6rem 0.8rem' }}>
            <FieldCompact label="Radiación solar (PVGIS)"
              value={siteData?.radiacion_pvgis?.irradiacion_anual_kwh_m2
                ? `${siteData.radiacion_pvgis.irradiacion_anual_kwh_m2} kWh/m²·año` : loadingPvgis ? '…' : '—'}
              small={siteData?.radiacion_pvgis?.fuente} />
            <FieldCompact label="Producción FV (1 kWp)"
              value={siteData?.radiacion_pvgis?.produccion_anual_kwh_kwp
                ? `${siteData.radiacion_pvgis.produccion_anual_kwh_kwp} kWh/año` : '—'}
              small={siteData?.radiacion_pvgis?.angle_used ? `Inclinación óptima ${siteData.radiacion_pvgis.angle_used}°` : ''} />
            <FieldCompact label="Factor emisión eléctrico"
              value={siteData?.factor_emision_electricidad_kg_kwh
                ? `${Number(siteData.factor_emision_electricidad_kg_kwh).toFixed(3)} kg CO₂/kWh` : '—'}
              small={siteData ? `IDAE · zona ${siteData.factor_emision_zona}` : ''} />
            <FieldCompact label="Potencial geotérmico"
              value={siteData?.potencial_geotermico_igme?.potencial_bt
                ? `BT ${siteData.potencial_geotermico_igme.potencial_bt} · MT ${siteData.potencial_geotermico_igme.potencial_mt}`
                : '—'}
              small={siteData?.potencial_geotermico_igme?.temperatura_media_terreno
                ? `Tª terreno ${siteData.potencial_geotermico_igme.temperatura_media_terreno} °C · IGME` : ''} />
          </div>
        )}
      </div>

      {/* Configuración de análisis CCV */}
      <div style={CARD}>
        <h3 style={SECTION_H}>
          <Calculator size={22} weight="regular" style={{ color: C.olive }} />
          Configuración de análisis económico
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '1rem' }}>
          <div>
            <label style={LABEL}>Horizonte del CCV (años)</label>
            <input type="number" value={horizonte} onChange={e => setHorizonte(Number(e.target.value))}
              style={INP} min={20} max={100} step={5} />
            <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: '0.3rem' }}>Norma UNE-EN 15459 · defecto 60</div>
          </div>
          <div>
            <label style={LABEL}>Tasa de descuento real (%)</label>
            <input type="number" value={tasa} onChange={e => setTasa(Number(e.target.value))}
              style={INP} min={0.5} max={10} step={0.1} />
            <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: '0.3rem' }}>Defecto 3 % · real, sobre inflación</div>
          </div>
          <div>
            <label style={LABEL}>Precio electricidad (€/kWh)</label>
            <input type="number" value={precioElec} onChange={e => setPrecioElec(Number(e.target.value))}
              style={INP} min={0.05} max={0.5} step={0.01} />
            <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: '0.3rem' }}>Autoconsumo terciario · defecto 0,15</div>
          </div>
          <div>
            <label style={LABEL}>Precio gas natural (€/kWh)</label>
            <input type="number" value={precioGas} onChange={e => setPrecioGas(Number(e.target.value))}
              style={INP} min={0.03} max={0.3} step={0.01} />
            <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: '0.3rem' }}>Sistema de referencia · defecto 0,08</div>
          </div>
          <div>
            <label style={LABEL}>Rendimiento sistema ref. (%)</label>
            <input type="number" value={rendRef} onChange={e => setRendRef(Number(e.target.value))}
              style={INP} min={70} max={100} step={1} />
            <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: '0.3rem' }}>Caldera gas natural · defecto 92</div>
          </div>
        </div>
        <div style={{ marginTop: '0.8rem', padding: '0.6rem 0.9rem',
                      background: C.cream, border: `1px solid ${C.border}`,
                      borderRadius: '5px', fontSize: '0.78rem', color: C.muted }}>
          <strong style={{ color: C.dark }}>Nota sobre el análisis · </strong>
          Las tecnologías térmicas (aerotermia, biomasa, geotermia, solar térmica) se comparan económicamente
          contra el sistema de referencia (caldera de gas natural). El ahorro anual es la diferencia entre lo que
          costaría producir la misma energía con gas natural vs lo que consume la tecnología B0C. Las tecnologías
          eléctricas (FV, eólica) se valoran por el autoconsumo/venta a precio de electricidad.
        </div>
      </div>

      {/* Notas del asesor */}
      <div style={CARD}>
        <h3 style={SECTION_H}>
          <PencilSimple size={22} weight="regular" style={{ color: C.olive }} />
          Notas del asesor
        </h3>
        <textarea rows={4} value={notas} onChange={e => setNotas(e.target.value)}
          style={{ ...INP, resize: 'vertical', minHeight: '80px' }}
          placeholder="Contexto del proyecto, restricciones del emplazamiento, cliente, etc." />
      </div>

      {/* Navegación */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <button style={BTN_G} onClick={guardar}>
          <FloppyDisk size={16} weight="regular" /> Guardar
        </button>
        <button style={BTN_P} onClick={continuar}>
          Continuar <ArrowRight size={16} weight="regular" />
        </button>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
//                  PANTALLA 2 · Selección de tecnologías
// ────────────────────────────────────────────────────────────────────────────
function Step2Technologies({ catalog, study, selectedTechs, setSelectedTechs, onBack, onNext, flash }) {
  const grupos = useMemo(() => {
    const g = { renovable_electrica: [], renovable_termica: [], hibrida_red: [] }
    for (const t of catalog) if (g[t.grupo]) g[t.grupo].push(t)
    return g
  }, [catalog])

  const toggleTech = async (tech) => {
    const isSel = !!selectedTechs[tech.id]
    if (isSel) {
      // Quitar
      const existing = selectedTechs[tech.id]
      if (existing?.id) {
        await supabase.from('boc_study_technologies').delete().eq('id', existing.id)
      }
      const next = { ...selectedTechs }
      delete next[tech.id]
      setSelectedTechs(next)
    } else {
      // Añadir
      const { data, error } = await supabase
        .from('boc_study_technologies')
        .insert({
          study_id: study.id, technology_id: tech.id,
          potencia_propuesta_kw: null, coste_estimado_eur: null,
          produccion_esperada_kwh_ano: null,
        })
        .select().single()
      if (error) { flash(`Error: ${error.message}`, 'err'); return }
      setSelectedTechs({ ...selectedTechs, [tech.id]: {
        id: data.id, potencia: null, coste: null, produccion: null, consumo: null,
      }})
    }
  }

  const updateTech = async (techId, field, value) => {
    const existing = selectedTechs[techId]
    if (!existing) return
    const num = value === '' ? null : Number(value)
    const patch = field === 'potencia'   ? { potencia: num }
               : field === 'coste'       ? { coste: num }
               : field === 'produccion'  ? { produccion: num }
               : field === 'consumo'     ? { consumo: num }
               : field === 'area'        ? { areaBruta: num }
               : {}
    const next = { ...selectedTechs, [techId]: { ...existing, ...patch } }
    setSelectedTechs(next)
    const dbPatch = field === 'potencia'   ? { potencia_propuesta_kw: num }
                 : field === 'coste'       ? { coste_estimado_eur: num }
                 : field === 'produccion'  ? { produccion_esperada_kwh_ano: num }
                 : field === 'consumo'     ? { consumo_electrico_kwh_ano: num }
                 : field === 'area'        ? { area_bruta_m2: num }
                 : {}
    await supabase.from('boc_study_technologies').update(dbPatch).eq('id', existing.id)
  }

  const nSelected = Object.keys(selectedTechs).length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={CARD}>
        <h3 style={SECTION_H}>
          <ListChecks size={22} weight="regular" style={{ color: C.olive }} />
          Tecnologías consideradas
        </h3>
        <p style={{ fontSize: '0.85rem', color: C.muted, marginTop: 0 }}>
          Selecciona las tecnologías B0C aplicables al proyecto. Recomendamos incluir al menos
          3 alternativas para hacer una comparativa razonable. Para cada tecnología introducirás
          la potencia propuesta, coste estimado y (si aplica) producción anual esperada.
        </p>

        {Object.entries(grupos).map(([grupoKey, techs]) => techs.length > 0 && (
          <GrupoBloque key={grupoKey} grupoKey={grupoKey} techs={techs}
            selectedTechs={selectedTechs}
            onToggle={toggleTech} onUpdate={updateTech} />
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button style={BTN_G} onClick={onBack}>
          <ArrowLeft size={16} weight="regular" /> Atrás
        </button>
        <div style={{ fontSize: '0.85rem', color: nSelected < 2 ? C.coral : C.muted }}>
          {nSelected} tecnología{nSelected === 1 ? '' : 's'} seleccionada{nSelected === 1 ? '' : 's'}
          {nSelected < 2 && ' · mínimo 2 para comparar'}
        </div>
        <button style={{ ...BTN_P, opacity: nSelected < 2 ? 0.5 : 1 }}
          disabled={nSelected < 2}
          onClick={onNext}>
          Continuar <ArrowRight size={16} weight="regular" />
        </button>
      </div>
    </div>
  )
}

function GrupoBloque({ grupoKey, techs, selectedTechs, onToggle, onUpdate }) {
  const nombreGrupo = {
    renovable_electrica: 'Renovables eléctricas',
    renovable_termica:   'Renovables térmicas',
    hibrida_red:         'Híbridas · red',
  }[grupoKey]
  const Icon = ICONO_GRUPO[grupoKey] || Lightning
  const b0cColor = { si: C.ok, parcial: C.amber, condicional: C.coral, emergente: C.muted }

  return (
    <div style={{ marginTop: '1.2rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem',
                    fontSize: '0.85rem', color: C.brown, fontWeight: 700 }}>
        <Icon size={18} weight="regular" style={{ color: C.olive }} />
        {nombreGrupo}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {techs.map(t => {
          const sel = !!selectedTechs[t.id]
          const data = selectedTechs[t.id]
          return (
            <div key={t.id} style={{
              padding: '0.75rem 1rem',
              background: sel ? '#F9F7F1' : C.cream,
              border: `1px solid ${sel ? C.olive : C.border}`,
              borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '0.5rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <input type="checkbox" checked={sel} onChange={() => onToggle(t)}
                  style={{ accentColor: C.olive, width: '16px', height: '16px' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.9rem', color: C.dark, fontWeight: 600 }}>{t.nombre}</div>
                  <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: '2px' }}>
                    {t.descripcion}
                  </div>
                </div>
                <span style={{
                  padding: '0.25rem 0.6rem', borderRadius: '999px', fontSize: '0.68rem',
                  background: `${b0cColor[t.breeam_b0c]}22`, color: b0cColor[t.breeam_b0c],
                  fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em',
                  border: `1px solid ${b0cColor[t.breeam_b0c]}55`,
                }}>{t.breeam_b0c}</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: C.muted, paddingLeft: '2rem' }}>
                Rango típico · {t.kw_min}-{t.kw_max} kW · {t.coste_kw_min}-{t.coste_kw_max} €/kW · vida {t.vida_util_min}-{t.vida_util_max} años
                {t.breeam_notas?.length > 0 && <> · NAs: {t.breeam_notas.join(', ')}</>}
              </div>
              {sel && (
                <div style={{ paddingLeft: '2rem', marginTop: '0.4rem' }}>
                  {(() => {
                    const esElectrica = t.tipo_energia === 'electrica'
                    const esTermica = t.tipo_energia === 'termica'
                    // Todas las FV tienen área bruta relevante · para térmicas también útil
                    // para solar térmica. Otras térmicas (aerotermia interna): opcional
                    const n = 3 + (esTermica ? 1 : 0) + 1   // +1 para el área bruta
                    return (
                      <div style={{ display: 'grid',
                                    gridTemplateColumns: `repeat(${n}, 1fr)`,
                                    gap: '0.6rem' }}>
                        <div>
                          <label style={LABEL}>Potencia propuesta (kW)</label>
                          <input type="number" value={data?.potencia ?? ''}
                            onChange={e => onUpdate(t.id, 'potencia', e.target.value)}
                            style={INP} placeholder={`${t.kw_min}-${t.kw_max}`} />
                        </div>
                        <div>
                          <label style={LABEL}>Coste estimado (€)</label>
                          <input type="number" value={data?.coste ?? ''}
                            onChange={e => onUpdate(t.id, 'coste', e.target.value)}
                            style={INP} placeholder={data?.potencia
                              ? `≈ ${(data.potencia * t.coste_kw_min).toLocaleString('es-ES')}-${(data.potencia * t.coste_kw_max).toLocaleString('es-ES')}`
                              : 'introduce primero potencia'} />
                        </div>
                        <div>
                          <label style={LABEL}>
                            {esElectrica
                              ? 'Producción eléctrica anual (kWh)'
                              : 'Producción térmica anual (kWh)'}
                          </label>
                          <input type="number" value={data?.produccion ?? ''}
                            onChange={e => onUpdate(t.id, 'produccion', e.target.value)}
                            style={INP}
                            placeholder={esTermica
                              ? 'calor + refr + ACS · kWh/año'
                              : 'kWh/año autoconsumidos'} />
                        </div>
                        {esTermica && (
                          <div>
                            <label style={LABEL}>Consumo eléctrico anual (kWh)</label>
                            <input type="number" value={data?.consumo ?? ''}
                              onChange={e => onUpdate(t.id, 'consumo', e.target.value)}
                              style={INP}
                              placeholder="kWh eléctricos que consume" />
                          </div>
                        )}
                        <div>
                          <label style={LABEL}>Área bruta (m²)</label>
                          <input type="number" value={data?.areaBruta ?? ''}
                            onChange={e => onUpdate(t.id, 'area', e.target.value)}
                            style={INP}
                            placeholder={esElectrica ? 'superficie de módulos' : 'superficie ocupada'} />
                        </div>
                      </div>
                    )
                  })()}
                  {t.tipo_energia === 'termica' && (
                    <div style={{ fontSize: '0.7rem', color: C.muted, marginTop: '0.4rem', fontStyle: 'italic' }}>
                      Para tecnologías térmicas · el ahorro económico se calcula comparando con el sistema de referencia
                      (caldera de gas natural 92 % rendimiento). El precio de gas de referencia y de la electricidad se
                      configuran en la Pantalla 1. El área bruta se usa para calcular kg CO₂/m² en el ACV.
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
//              PANTALLA 3 · Matriz comparativa + CCV
// ────────────────────────────────────────────────────────────────────────────
function Step3Comparison({ catalog, study, siteData, selectedTechs, setSelectedTechs, ccvResults, setCcvResults, acvResults, setAcvResults, onBack, onNext, flash }) {
  const [computingCcv, setComputingCcv] = useState(false)
  const [computingAcv, setComputingAcv] = useState(false)

  // ─── ACV completo · UNE-EN ISO 14044:2006 (Fase 2.6) ────────────────────
  // Estados que consumen las funciones RPC creadas en las migraciones 2.4-2.5c:
  //   - boc_acv_resumen_por_categoria · agregado por tech × categoría
  //   - boc_acv_completo_por_estudio  · matriz completa tech × fase × categoría
  //   - boc_acv_cobertura_por_estudio · qué tec tienen EPD, cuáles BDs disponibles
  const [acvResumen, setAcvResumen] = useState([])
  const [acvMatriz, setAcvMatriz] = useState([])
  const [acvCobertura, setAcvCobertura] = useState([])
  const [expandedTech, setExpandedTech] = useState(null)
  const [computingAcvCompleto, setComputingAcvCompleto] = useState(false)

  // Auto-recalcular CCV al entrar en Pantalla 3 · silenciosamente si ya había resultados
  // (así los datos siempre reflejan los últimos cambios en Pantalla 2, sin que el usuario
  // tenga que pulsar Recalcular)
  useEffect(() => {
    if (!study.id) return
    let cancelled = false
    async function refreshCcv() {
      const { data } = await supabase.rpc('boc_ccv_por_estudio', { p_study_id: study.id })
      if (!cancelled && data) setCcvResults(data)
    }
    refreshCcv()
    return () => { cancelled = true }
  }, [study.id, setCcvResults])

  // Enriquecer los resultados del ACV con datos del catálogo + 3 escenarios:
  // 1. Genérico ÖKOBAUDAT/Ecoinvent (motor propio · lo que ya calculamos)
  // 2. OneClick LCA (si el asesor lo introdujo manualmente)
  // 3. EPD real del proyecto · A1-A3 GWP-total de boc_acv_factors
  //    (jerarquía 1 = DAP subida proyecto · 2-4 = EPD alternativa de BD pública)
  const [acvEpdReal, setAcvEpdReal] = useState({})   // { tech_key: { valor_por_kw, fuente, jerarquia, fuente_bd, fabricante } }
  const acvEnriched = useMemo(() => {
    return acvResults.map(r => {
      const tech = catalog.find(t => t.id === r.technology_id)
      const stech = selectedTechs[r.technology_id]
      const pot = stech?.potencia || 0
      const gwpKw = Number(r.gwp_a1_a3_kg_co2_por_kw) || 0
      const gwpOneclick = r.oneclick_lca_valor ? Number(r.oneclick_lca_valor) : null

      // Escenario EPD real · prioridad:
      //   1) Valor A1-A3 GWP-total real de boc_acv_factors (jerarquía 1-4 según EPD)
      //      · si es DAP subida del proyecto (jerarquía 1) es la más precisa
      //      · si es EPD alternativa (jerarquía 2-4) también es válida
      //   2) Valor específico introducido por el asesor (gwpEpdEspecifico · retrocompat)
      //   3) Media del rango del catálogo antiguo (co2_embebido_min_epd / max_epd)
      //   4) null si no hay ninguno
      let gwpEpd = null
      let epdEsFallback = false
      let epdFuenteReal = null       // texto para mostrar bajo el número
      const epdReal = tech?.tech_key && acvEpdReal[tech.tech_key]
      if (epdReal) {
        gwpEpd = epdReal.valor_por_kw
        epdFuenteReal = `${epdReal.fabricante || 'EPD'} · ${epdReal.fuente_bd || epdReal.fuente || ''}`
      } else if (stech?.gwpEpdEspecifico) {
        gwpEpd = Number(stech.gwpEpdEspecifico)
      } else if (tech?.co2_embebido_min_epd && tech?.co2_embebido_max_epd) {
        gwpEpd = (Number(tech.co2_embebido_min_epd) + Number(tech.co2_embebido_max_epd)) / 2
        epdEsFallback = true   // ojo · es la media del rango, no un valor específico
      }

      return {
        ...r,
        tech_key: tech?.tech_key,
        nombre: tech?.nombre || r.tech_key,
        potencia_kw: pot,
        // Escenario 1 · genérico
        gwp_generico_kw: gwpKw,
        gwp_generico_total: gwpKw && pot ? gwpKw * pot : null,
        gwp_generico_min: tech?.co2_embebido_min,
        gwp_generico_max: tech?.co2_embebido_max,
        // Escenario 2 · OneClick LCA
        gwp_oneclick_kw: gwpOneclick,
        gwp_oneclick_total: gwpOneclick && pot ? gwpOneclick * pot : null,
        // Escenario 3 · EPD real proyecto (viene de boc_acv_factors si hay dato)
        gwp_epd_kw: gwpEpd,
        gwp_epd_total: gwpEpd && pot ? gwpEpd * pot : null,
        gwp_epd_min: tech?.co2_embebido_min_epd,
        gwp_epd_max: tech?.co2_embebido_max_epd,
        epd_es_fallback: epdEsFallback,
        epd_fuente_real: epdFuenteReal,     // ej. "JA Solar · EPD-Norway", "Vaillant · otras", "Daikin · null"
        // EPD de referencia del catálogo (siempre disponible como fallback)
        epd_url_catalogo: tech?.epd_url_ejemplo,
        epd_descripcion_catalogo: tech?.epd_descripcion_ejemplo,
        // EPD específica del proyecto (introducida por el asesor)
        epd_url_proyecto: stech?.epdUrlProyecto,
        epd_descripcion_proyecto: stech?.epdDescripcionProyecto,
        // Notas sobre la discrepancia entre genérico vs EPD
        co2_notas: tech?.co2_embebido_notas,
      }
    })
  }, [acvResults, catalog, selectedTechs, acvEpdReal])

  // Guardar valor OneClick LCA · el asesor introduce el kg CO2 TOTAL (como sale
  // del Excel de OneClick) y la app divide por la potencia para guardar en kg/kW
  const setOneclickTotal = async (techId, valorTotal) => {
    const numTotal = valorTotal === '' ? null : Number(valorTotal)
    const stech = selectedTechs[techId]
    const pot = Number(stech?.potencia) || 0
    if (numTotal && !pot) {
      flash('Necesitas introducir potencia en Pantalla 2 antes de guardar OneClick', 'err')
      return
    }
    const numPorKw = numTotal ? numTotal / pot : null
    // Actualizar optimista en local
    const nextResults = acvResults.map(r => r.technology_id === techId
      ? { ...r, oneclick_lca_valor: numPorKw, oneclick_lca_fecha: numPorKw ? new Date().toISOString().slice(0,10) : null,
          desviacion_pct: numPorKw && r.gwp_a1_a3_kg_co2_por_kw
            ? Math.round(((Number(r.gwp_a1_a3_kg_co2_por_kw) - numPorKw) / numPorKw * 100) * 100) / 100
            : null }
      : r)
    setAcvResults(nextResults)
    if (numPorKw) {
      const { error } = await supabase.rpc('boc_registrar_oneclick', {
        p_study_id: study.id,
        p_technology_id: techId,
        p_valor_kg_co2_kw: numPorKw,
      })
      if (error) flash(`Error al guardar OneClick: ${error.message}`, 'err')
    } else {
      const { error } = await supabase.from('boc_shadow_lca')
        .update({ oneclick_lca_valor: null, oneclick_lca_fecha: null })
        .eq('study_id', study.id).eq('technology_id', techId)
      if (error) flash(`Error al borrar OneClick: ${error.message}`, 'err')
    }
  }

  // Guardar EPD específica del proyecto · asesor introduce el kg CO2 TOTAL para
  // toda la instalación (como sale de la EPD del fabricante multiplicado por
  // potencia/superficie de la instalación) y la app divide por potencia para
  // guardar en kg/kW
  const setEpdTotal = async (techId, valorTotal) => {
    const existing = selectedTechs[techId]
    if (!existing?.id) return
    const numTotal = valorTotal === '' ? null : Number(valorTotal)
    const pot = Number(existing?.potencia) || 0
    if (numTotal && !pot) {
      flash('Necesitas introducir potencia en Pantalla 2 antes de guardar EPD específica', 'err')
      return
    }
    const numPorKw = numTotal ? numTotal / pot : null
    const next = { ...selectedTechs, [techId]: { ...existing, gwpEpdEspecifico: numPorKw } }
    setSelectedTechs(next)
    const { error } = await supabase.from('boc_study_technologies')
      .update({ gwp_epd_especifico_kg_kw: numPorKw })
      .eq('id', existing.id)
    if (error) flash(`Error al guardar EPD específica: ${error.message}`, 'err')
  }

  const techsList = useMemo(() =>
    catalog.filter(t => selectedTechs[t.id]).map(t => ({ ...t, sel: selectedTechs[t.id] })),
  [catalog, selectedTechs])

  // Toggle "aplicable / no aplicable" · guarda inmediato en BD y estado local
  const toggleAplicable = async (techId, esAplicable) => {
    const existing = selectedTechs[techId]
    if (!existing?.id) return
    const next = { ...selectedTechs, [techId]: { ...existing, esAplicable } }
    setSelectedTechs(next)
    const { error } = await supabase.from('boc_study_technologies')
      .update({ es_aplicable: esAplicable })
      .eq('id', existing.id)
    if (error) flash(`Error al guardar: ${error.message}`, 'err')
  }

  const updateMotivoNoAplicable = async (techId, motivo) => {
    const existing = selectedTechs[techId]
    if (!existing?.id) return
    const next = { ...selectedTechs, [techId]: { ...existing, motivoNoAplicable: motivo } }
    setSelectedTechs(next)
    const { error } = await supabase.from('boc_study_technologies')
      .update({ motivo_no_aplicable: motivo || null })
      .eq('id', existing.id)
    if (error) flash(`Error al guardar: ${error.message}`, 'err')
  }

  const calcularCcv = async () => {
    setComputingCcv(true)
    try {
      const { data, error } = await supabase.rpc('boc_ccv_por_estudio', { p_study_id: study.id })
      if (error) throw error
      setCcvResults(data || [])
    } catch (e) {
      flash(`Error CCV: ${e.message}`, 'err')
    } finally {
      setComputingCcv(false)
    }
  }

  const calcularAcv = async () => {
    setComputingAcv(true)
    try {
      const { data, error } = await supabase.rpc('boc_poblar_shadow_lca', { p_study_id: study.id })
      if (error) throw error
      // Sólo guardamos los campos raw · el enriquecimiento lo hace useMemo(acvEnriched)
      setAcvResults(data || [])
      flash(`ACV calculado para ${data?.length || 0} tecnologías`, 'ok')
    } catch (e) {
      flash(`Error ACV: ${e.message}`, 'err')
    } finally {
      setComputingAcv(false)
    }
  }

  // ─── ACV completo · UNE-EN ISO 14044:2006 ────────────────────────────────
  // Carga automática del resumen y cobertura al entrar en Pantalla 3.
  // La matriz completa (13×15) se pide bajo demanda al expandir una tecnología.
  useEffect(() => {
    if (!study.id) return
    let cancelled = false
    async function loadAcvCompleto() {
      const [{ data: resumen }, { data: cobertura }] = await Promise.all([
        supabase.rpc('boc_acv_resumen_por_categoria',
          { p_study_id: study.id, p_unidad_funcional: 'kW' }),
        supabase.rpc('boc_acv_cobertura_por_estudio',
          { p_study_id: study.id }),
      ])
      if (cancelled) return
      if (resumen) setAcvResumen(resumen)
      if (cobertura) setAcvCobertura(cobertura)
    }
    loadAcvCompleto()
    return () => { cancelled = true }
  }, [study.id])

  // Mapa de EPDs subidas al Storage por tech_key · para el botón "Descargar"
  // en la columna "EPD ref." de la tabla ACV embebido. Se genera signed URL
  // bajo demanda al hacer clic en el enlace (más seguro que URL pública).
  const [epdDownloads, setEpdDownloads] = useState({})   // { tech_key: { storage_path, fabricante } }
  useEffect(() => {
    if (!study.id) return
    let cancelled = false
    async function loadDownloads() {
      // Filtramos storage_paths reales (no los stubs 'no-file/', 'no-epd/' o 'epd-search/')
      const { data } = await supabase
        .from('boc_epd_uploads')
        .select('technology_id, storage_path, fabricante, modelo, created_at, boc_technology_catalog!inner(tech_key)')
        .eq('study_id', study.id)
        .not('storage_path', 'like', 'no-file/%')
        .not('storage_path', 'like', 'no-epd/%')
        .not('storage_path', 'like', 'epd-search/%')
        .order('created_at', { ascending: false })
      if (cancelled) return
      const byTech = {}
      for (const row of (data || [])) {
        const techKey = row.boc_technology_catalog?.tech_key
        if (techKey && !byTech[techKey]) {
          byTech[techKey] = {
            storage_path: row.storage_path,
            fabricante: row.fabricante,
            modelo: row.modelo,
          }
        }
      }
      setEpdDownloads(byTech)
    }
    loadDownloads()
    return () => { cancelled = true }
  }, [study.id, acvResumen])

  // Al pulsar "Descargar" genera signed URL válida 60 seg y abre en pestaña nueva
  const descargarEpdSubida = async (techKey) => {
    const info = epdDownloads[techKey]
    if (!info) return
    const { data, error } = await supabase.storage
      .from('boc-epd-uploads')
      .createSignedUrl(info.storage_path, 60)
    if (error) { flash(`No se pudo generar el enlace: ${error.message}`, 'err'); return }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
  }

  // GWP-total A1-A3 por tecnología desde boc_acv_factors · sirve para alimentar
  // la columna "EPD real proyecto" de la tabla ACV embebido con datos reales
  // de las EPDs cargadas (JA Solar, Vaillant, Daikin subida, etc.), en vez de
  // los valores promedio min-max del catálogo antiguo.
  //
  // Preferencia: el motor toma la fila de menor jerarquía disponible por celda
  // (1=DAP subida proyecto → 2=DAP alternativa BD → 3=sectorial → 4=genérico BD).
  useEffect(() => {
    if (!study.id) return
    let cancelled = false
    async function loadEpdReal() {
      const { data } = await supabase
        .from('boc_acv_factors')
        .select('tech_key, valor, fuente_referencia, fuente_bd, jerarquia, ano_dato, fabricante')
        .eq('fase_codigo', 'A1-A3')
        .eq('categoria_codigo', 'GWP-total')
        .eq('unidad_funcional', 'kW')
        .order('jerarquia', { ascending: true })
        .order('ano_dato', { ascending: false, nullsFirst: false })
      if (cancelled) return
      const byTech = {}
      for (const row of (data || [])) {
        if (!byTech[row.tech_key]) {
          byTech[row.tech_key] = {
            valor_por_kw: Number(row.valor),
            fuente: row.fuente_referencia,
            fuente_bd: row.fuente_bd,
            jerarquia: row.jerarquia,
            fabricante: row.fabricante,
          }
        }
      }
      setAcvEpdReal(byTech)
    }
    loadEpdReal()
    return () => { cancelled = true }
  }, [study.id, acvResumen])   // recarga cuando cambia acvResumen (tras subir EPD nueva)

  const recalcularAcvCompleto = async () => {
    if (!study.id) return
    setComputingAcvCompleto(true)
    try {
      const [{ data: resumen, error: e1 }, { data: cobertura, error: e2 }] = await Promise.all([
        supabase.rpc('boc_acv_resumen_por_categoria',
          { p_study_id: study.id, p_unidad_funcional: 'kW' }),
        supabase.rpc('boc_acv_cobertura_por_estudio',
          { p_study_id: study.id }),
      ])
      if (e1) throw e1
      if (e2) throw e2
      setAcvResumen(resumen || [])
      setAcvCobertura(cobertura || [])
      setAcvMatriz([])
      setExpandedTech(null)
      flash('ACV completo actualizado', 'ok')
    } catch (e) {
      flash(`Error al recalcular ACV completo: ${e.message}`, 'err')
    } finally {
      setComputingAcvCompleto(false)
    }
  }

  const cargarMatrizTecnologia = async (techId) => {
    if (expandedTech === techId) {
      setExpandedTech(null)
      return
    }
    setExpandedTech(techId)
    // Si ya tenemos la matriz cargada de todo el estudio no repetimos la llamada
    if (acvMatriz.length > 0) return
    const { data, error } = await supabase.rpc('boc_acv_completo_por_estudio',
      { p_study_id: study.id, p_unidad_funcional: 'kW' })
    if (error) { flash(`Error matriz ACV: ${error.message}`, 'err'); return }
    setAcvMatriz(data || [])
  }

  // Solo participan en los rankings las tecnologías marcadas como aplicables
  const techsAplicables = techsList.filter(t => t.sel?.esAplicable !== false)

  const rankingCcvUnitario = useMemo(() => {
    return ccvResults
      .filter(r => techsAplicables.find(t => t.id === r.technology_id) && r.ccv_por_kw != null)
      .sort((a, b) => Number(a.ccv_por_kw) - Number(b.ccv_por_kw))
  }, [ccvResults, techsAplicables])

  const rankingCcvTotal = useMemo(() => {
    return ccvResults
      .filter(r => techsAplicables.find(t => t.id === r.technology_id) && r.ccv_total_eur != null)
      .sort((a, b) => Number(a.ccv_total_eur) - Number(b.ccv_total_eur))
  }, [ccvResults, techsAplicables])

  const rankingGwpUnitario = useMemo(() => {
    return acvEnriched
      .filter(r => techsAplicables.find(t => t.id === r.technology_id) && r.gwp_a1_a3_kg_co2_por_kw != null)
      .sort((a, b) => Number(a.gwp_a1_a3_kg_co2_por_kw) - Number(b.gwp_a1_a3_kg_co2_por_kw))
  }, [acvEnriched, techsAplicables])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Aplicabilidad de cada tecnología · toggle Sí/No + motivo textual */}
      <div style={CARD}>
        <h3 style={SECTION_H}>
          <ListChecks size={20} weight="regular" style={{ color: C.olive }} />
          Aplicabilidad de cada tecnología
        </h3>
        <p style={{ fontSize: '0.78rem', color: C.muted, marginTop: 0, marginBottom: '0.9rem' }}>
          Marca cada tecnología como aplicable o no al proyecto. Las descartadas necesitan un motivo técnico
          concreto (ej. "sin recurso eólico", "sin espacio en cubierta"). Solo las aplicables participan en los
          rankings de comparación.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {techsList.map(t => {
            const aplicable = t.sel?.esAplicable !== false
            return (
              <div key={t.id} style={{
                padding: '0.5rem 0.75rem',
                background: aplicable ? '#F1F5E8' : '#FDF5F3',
                border: `1px solid ${aplicable ? C.ok : C.coral}`,
                borderRadius: '5px', display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
              }}>
                <div style={{ display: 'flex', gap: '0.4rem', paddingTop: '2px' }}>
                  <button onClick={() => toggleAplicable(t.id, true)}
                    style={{
                      padding: '0.25rem 0.7rem',
                      background: aplicable ? C.ok : 'transparent',
                      color: aplicable ? '#fff' : C.muted,
                      border: `1px solid ${aplicable ? C.ok : C.border}`,
                      borderRadius: '4px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
                    }}>Aplicable</button>
                  <button onClick={() => toggleAplicable(t.id, false)}
                    style={{
                      padding: '0.25rem 0.7rem',
                      background: !aplicable ? C.coral : 'transparent',
                      color: !aplicable ? '#fff' : C.muted,
                      border: `1px solid ${!aplicable ? C.coral : C.border}`,
                      borderRadius: '4px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
                    }}>Descartar</button>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.85rem', color: C.dark, fontWeight: 600 }}>{t.nombre}</div>
                  {!aplicable && (
                    <input type="text"
                      value={t.sel?.motivoNoAplicable ?? ''}
                      onChange={e => updateMotivoNoAplicable(t.id, e.target.value)}
                      placeholder="Motivo técnico del descarte (ej. sin recurso eólico en el emplazamiento)"
                      style={{ ...INP, marginTop: '0.35rem', background: '#fff' }} />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* CCV */}
      <div style={CARD}>
        <h3 style={SECTION_H}>
          <Files size={22} weight="regular" style={{ color: C.olive }} />
          Coste del Ciclo de Vida (60 años)
          <button style={{ ...BTN_G, marginLeft: 'auto' }}
            onClick={calcularCcv} disabled={computingCcv}>
            {computingCcv ? <Spinner size={14} weight="regular" /> : <Calculator size={14} weight="regular" />}
            Recalcular CCV
          </button>
        </h3>
        {ccvResults.length === 0 && (
          <div style={{ padding: '1rem', color: C.muted, fontSize: '0.85rem' }}>
            Haz clic en <em>Recalcular CCV</em> para obtener los costes a 60 años de cada tecnología.
          </div>
        )}
        {ccvResults.length > 0 && (
          <>
            <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Tecnología</th>
                  <th style={thStyle}>Potencia (kW)</th>
                  <th style={thStyle}>CAPEX (€)</th>
                  <th style={thStyle}>OPEX actualizado (€)</th>
                  <th style={thStyle}>Reemplazos</th>
                  <th style={thStyle}>CCV total (€)</th>
                  <th style={thStyle}>CCV/kW (€)</th>
                </tr>
              </thead>
              <tbody>
                {ccvResults.map(r => (
                  <tr key={r.technology_id}>
                    <td style={{ ...tdStyle, textAlign: 'left' }}>{r.nombre}</td>
                    <td style={tdStyle}>{r.potencia_kw || '—'}</td>
                    <td style={tdStyle}>{fmtEur(r.capex_eur)}</td>
                    <td style={tdStyle}>{fmtEur(r.opex_mant_actualizado_eur)}</td>
                    <td style={tdStyle}>{r.n_reemplazos}</td>
                    <td style={{ ...tdStyle, fontWeight: 700, color: r.ccv_total_eur < 0 ? C.ok : C.dark }}>
                      {fmtEur(r.ccv_total_eur)}
                    </td>
                    <td style={tdStyle}>{r.ccv_por_kw ? fmtEur(r.ccv_por_kw) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Bloque explicativo · cómo se calcula el CCV */}
            <div style={{ marginTop: '1rem', padding: '0.75rem 0.9rem',
                          background: C.cream, border: `1px solid ${C.border}`,
                          borderRadius: '5px', fontSize: '0.75rem', color: C.dark,
                          lineHeight: '1.55' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: C.dark, marginBottom: '0.4rem' }}>
                Cómo se calcula el CCV
              </div>
              <p style={{ margin: '0 0 0.5rem' }}>
                Método <strong>UNE-EN 15459:2008</strong> · Prestaciones energéticas de los edificios · Procedimiento de evaluación económica de los sistemas energéticos. Fórmula global:
              </p>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.72rem',
                            background: '#fff', padding: '0.4rem 0.6rem', borderRadius: '3px',
                            border: `1px solid ${C.border}`, marginBottom: '0.6rem',
                            color: C.brown }}>
                CCV = CAPEX + Σ(OPEX_t) + Σ(reemplazos_t) − Σ(beneficio energético_t)
              </div>
              <p style={{ margin: '0 0 0.4rem' }}>
                <strong>Cada columna</strong>:
              </p>
              <ul style={{ margin: '0 0 0.6rem', paddingLeft: '1.2rem' }}>
                <li><strong>CAPEX</strong> · inversión inicial en año 0 (el valor que introdujiste en Pantalla 2)</li>
                <li><strong>OPEX actualizado</strong> · mantenimiento anual estimado (<strong>1,5 % del CAPEX/año</strong>) descontado al valor presente durante los {study.horizonte_ccv_anos || 60} años del horizonte</li>
                <li><strong>Reemplazos</strong> · nº de veces que hay que sustituir el equipo según su vida útil (del catálogo). El coste de cada reemplazo se descuenta también al año 0</li>
                <li><strong>CCV total</strong> · resultado neto. <span style={{ color: C.ok, fontWeight: 600 }}>Negativo</span> = tecnología rentable (ahorro neto en 60 años vs no hacer nada o vs sistema de referencia). <span style={{ color: C.dark, fontWeight: 600 }}>Positivo</span> = coste neto</li>
                <li><strong>CCV/kW</strong> · CCV total dividido por la potencia · útil para comparar tecnologías de tamaño distinto en igualdad de condiciones</li>
              </ul>
              <p style={{ margin: '0 0 0.4rem' }}>
                <strong>Beneficio energético</strong> · varía según el tipo de tecnología:
              </p>
              <ul style={{ margin: '0 0 0.6rem', paddingLeft: '1.2rem' }}>
                <li><strong>Eléctricas</strong> (FV, eólica) · beneficio = producción × precio electricidad {Number(study.precio_electricidad_eur_kwh || 0.15).toFixed(2)} €/kWh. Todo lo producido se considera autoconsumido</li>
                <li><strong>Térmicas</strong> (aerotermia, biomasa, geotermia, solar térmica) · beneficio = ahorro vs sistema de referencia (caldera de gas natural con rendimiento {study.rendimiento_sistema_ref_pct || 92} %). Coste operativo = consumo eléctrico × precio electricidad. Ahorro = (producción térmica / rendimiento_ref) × precio gas {Number(study.precio_gas_referencia_eur_kwh || 0.08).toFixed(2)} €/kWh</li>
              </ul>
              <p style={{ margin: '0' }}>
                <strong>Parámetros del análisis</strong> ·
                horizonte {study.horizonte_ccv_anos || 60} años ·
                tasa de descuento real {Number(study.tasa_descuento_pct || 3).toFixed(1)} % ·
                escalado precio energía +2 %/año (real, sobre inflación) ·
                todos los flujos descontados al valor presente año 0. Los precios y la tasa se editan en la Pantalla 1.
              </p>
            </div>
          </>
        )}
      </div>

      {/* ACV · Carbono embebido (fases A1-A3 de EN 15978) */}
      <div style={CARD}>
        <h3 style={SECTION_H}>
          <Leaf size={22} weight="regular" style={{ color: C.olive }} />
          Análisis de Ciclo de Vida · carbono embebido
          <button style={{ ...BTN_G, marginLeft: 'auto' }}
            onClick={calcularAcv} disabled={computingAcv}>
            {computingAcv ? <Spinner size={14} weight="regular" /> : <Calculator size={14} weight="regular" />}
            {acvResults.length === 0 ? 'Calcular ACV embebido' : 'Recalcular ACV'}
          </button>
        </h3>
        <p style={{ fontSize: '0.82rem', color: C.muted, marginTop: 0, marginBottom: '0.9rem' }}>
          Emisiones de CO₂ generadas al fabricar e instalar cada tecnología (fases A1-A3 de la norma UNE-EN 15978).
        </p>
        {acvResults.length === 0 && (
          <div style={{ padding: '1rem', color: C.muted, fontSize: '0.85rem' }}>
            Haz clic en <em>Calcular ACV embebido</em> para obtener las emisiones de fabricación e instalación
            de las tecnologías seleccionadas.
          </div>
        )}
        {acvEnriched.length > 0 && (
          <>
            {/* Tabla de 3 escenarios · introduces valor TOTAL, la app muestra por kW y por m² */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Tecnología<br/><span style={{ color: C.muted, fontWeight: 400 }}>kW · m²</span></th>
                    <th style={{ ...thStyle, background: '#EDE4D0' }}>Genérico ÖKOBAUDAT<br/><span style={{ color: C.muted, fontWeight: 400 }}>kg CO₂ total</span></th>
                    <th style={{ ...thStyle, background: '#E4E9D0' }}>OneClick LCA<br/><span style={{ color: C.muted, fontWeight: 400 }}>kg CO₂ total · editable</span></th>
                    <th style={{ ...thStyle, background: '#D0E4D8' }}>EPD real proyecto<br/><span style={{ color: C.muted, fontWeight: 400 }}>kg CO₂ total · editable</span></th>
                    <th style={thStyle}>EPD ref.</th>
                  </tr>
                </thead>
                <tbody>
                  {acvEnriched.map(r => {
                    const stech = selectedTechs[r.technology_id]
                    const area = Number(stech?.areaBruta) || 0
                    const pot = Number(r.potencia_kw) || 0
                    return (
                      <tr key={r.technology_id}>
                        {/* Nombre + potencia + área */}
                        <td style={{ ...tdStyle, textAlign: 'left' }}>
                          <div style={{ fontWeight: 600 }}>{r.nombre}</div>
                          <div style={{ fontSize: '0.72rem', color: C.muted, fontFamily: "'IBM Plex Mono', monospace" }}>
                            {pot ? `${pot} kW` : '— kW'} · {area ? `${area} m²` : '— m²'}
                          </div>
                        </td>
                        {/* Genérico ÖKOBAUDAT · no editable · Total (grande) + kg/kW + kg/m² */}
                        <td style={{ ...tdStyle, background: '#FAF6EE' }}>
                          <CellMultiVista total={r.gwp_generico_total} porKw={r.gwp_generico_kw} area={area} />
                        </td>
                        {/* OneClick · editable · input pide TOTAL */}
                        <td style={{ ...tdStyle, background: '#F5F7ED', padding: '0.2rem', verticalAlign: 'top' }}>
                          <CellEditableMultiVista
                            total={r.gwp_oneclick_total}
                            porKw={r.gwp_oneclick_kw}
                            area={area}
                            onChange={val => setOneclickTotal(r.technology_id, val)}
                            placeholder=""
                          />
                        </td>
                        {/* EPD real · viene de boc_acv_factors (subida o alternativa) */}
                        <td style={{ ...tdStyle, background: '#EEF6F0', padding: '0.2rem', verticalAlign: 'top' }}>
                          <CellEditableMultiVista
                            total={r.gwp_epd_total}
                            porKw={r.gwp_epd_kw}
                            area={area}
                            editableValue={stech?.gwpEpdEspecifico != null && pot ? stech.gwpEpdEspecifico * pot : null}
                            onChange={val => setEpdTotal(r.technology_id, val)}
                            placeholderHint={r.epd_es_fallback && r.gwp_epd_kw && pot ? Math.round(r.gwp_epd_kw * pot) : null}
                            isFallback={r.epd_es_fallback}
                          />
                          {r.epd_fuente_real && (
                            <div style={{ fontSize: '0.68rem', color: C.muted, marginTop: '0.2rem',
                                          textAlign: 'center', fontStyle: 'italic' }}
                                 title={`Fuente A1-A3 GWP-total desde boc_acv_factors`}>
                              {r.epd_fuente_real}
                            </div>
                          )}
                        </td>
                        {/* Descarga EPD · prioridad:
                              1. EPD subida al Storage (más fresca, la del proyecto)
                              2. URL del proyecto (introducida en Pantalla 2)
                              3. URL del catálogo (EPD de referencia genérica)
                              4. Guión si no hay ninguna */}
                        <td style={{ ...tdStyle }}>
                          {epdDownloads[r.tech_key] ? (
                            <button
                              onClick={() => descargarEpdSubida(r.tech_key)}
                              title={`Descargar EPD subida · ${epdDownloads[r.tech_key].fabricante}${epdDownloads[r.tech_key].modelo ? ' · ' + epdDownloads[r.tech_key].modelo : ''}`}
                              style={{ background: 'transparent', border: 'none',
                                       color: C.olive, cursor: 'pointer',
                                       fontSize: '0.75rem', fontWeight: 600,
                                       textDecoration: 'none', padding: 0 }}>
                              Descargar
                            </button>
                          ) : (r.epd_url_proyecto || r.epd_url_catalogo) ? (
                            <a href={r.epd_url_proyecto || r.epd_url_catalogo}
                              target="_blank" rel="noopener noreferrer"
                              title={r.epd_descripcion_proyecto || r.epd_descripcion_catalogo || 'Descargar EPD de referencia'}
                              style={{ color: C.olive, textDecoration: 'none', fontSize: '0.75rem', fontWeight: 600 }}>
                              Descargar
                            </a>
                          ) : (
                            <span style={{ color: C.muted, fontSize: '0.72rem' }}>—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                  <tr style={{ background: '#F9F7F1' }}>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>Total del estudio</td>
                    <td style={{ ...tdStyle, background: '#FAF6EE', fontWeight: 700 }}>
                      {acvEnriched.reduce((s, r) => s + (Number(r.gwp_generico_total) || 0), 0).toLocaleString('es-ES', { maximumFractionDigits: 0 })} kg
                    </td>
                    <td style={{ ...tdStyle, background: '#F5F7ED', fontWeight: 700 }}>
                      {acvEnriched.reduce((s, r) => s + (Number(r.gwp_oneclick_total) || 0), 0).toLocaleString('es-ES', { maximumFractionDigits: 0 })} kg
                    </td>
                    <td style={{ ...tdStyle, background: '#EEF6F0', fontWeight: 700, color: C.ok }}>
                      {acvEnriched.reduce((s, r) => s + (Number(r.gwp_epd_total) || 0), 0).toLocaleString('es-ES', { maximumFractionDigits: 0 })} kg
                    </td>
                    <td style={tdStyle}></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Explicación dinámica · calcula las desviaciones reales del estudio */}
            <ExplicacionEscenariosDinamica acvEnriched={acvEnriched} />

            {/* Comparación motor propio vs OneClick si hay datos */}
            {acvEnriched.some(r => r.gwp_oneclick_kw) && (
              <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.9rem',
                            background: C.cream, border: `1px solid ${C.border}`,
                            borderRadius: '5px', fontSize: '0.75rem' }}>
                <strong style={{ color: C.dark }}>Desviación motor propio vs OneClick LCA</strong>
                <table style={{ width: '100%', marginTop: '0.4rem', fontSize: '0.75rem' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, textAlign: 'left' }}>Tecnología</th>
                      <th style={thStyle}>Motor propio</th>
                      <th style={thStyle}>OneClick</th>
                      <th style={thStyle}>Desviación</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acvEnriched.filter(r => r.gwp_oneclick_kw).map(r => (
                      <tr key={r.technology_id}>
                        <td style={{ ...tdStyle, textAlign: 'left' }}>{r.nombre}</td>
                        <td style={tdStyle}>{Math.round(r.gwp_generico_kw)}</td>
                        <td style={tdStyle}>{Math.round(r.gwp_oneclick_kw)}</td>
                        <td style={{ ...tdStyle,
                          color: Math.abs(r.desviacion_pct) < 15 ? C.ok : C.coral,
                          fontWeight: 700 }}>
                          {r.desviacion_pct > 0 ? '+' : ''}{r.desviacion_pct}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Bloque explicativo movido a modal desplegable desde el icono
                'i' del título de "ACV completo · UNE-EN ISO 14044:2006".
                Ver componente MetodologiaAcvModal más abajo. */}
          </>
        )}
      </div>

      {/* ACV completo · UNE-EN ISO 14044:2006 · 13 categorías × 15 fases */}
      <AcvCompletoBloque
        study={study}
        selectedTechs={selectedTechs}
        siteData={siteData}
        catalog={catalog}
        acvResumen={acvResumen}
        acvMatriz={acvMatriz}
        acvCobertura={acvCobertura}
        expandedTech={expandedTech}
        computing={computingAcvCompleto}
        onRecalcular={recalcularAcvCompleto}
        onToggleExpand={cargarMatrizTecnologia}
        flash={flash}
      />

      {/* Rankings objetivos · 3 criterios cuantitativos */}
      {(rankingCcvUnitario.length > 0 || rankingGwpUnitario.length > 0) && (
        <div style={CARD}>
          <h3 style={SECTION_H}>
            <CheckCircle size={20} weight="regular" style={{ color: C.olive }} />
            Rankings objetivos
          </h3>
          <p style={{ fontSize: '0.78rem', color: C.muted, marginTop: 0, marginBottom: '0.9rem' }}>
            Basados solo en cifras calculadas. La recomendación final del especialista se escribe en la Pantalla 4
            de firma · combinando estos datos con criterios cualitativos del proyecto (integración, disponibilidad
            de recursos, etc.).
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: '0.9rem' }}>
            {rankingCcvUnitario.length > 0 && (
              <RankingBlock
                title="Mejor eficiencia económica"
                subtitle="CCV/kW · más negativo = más ahorro por kW"
                items={rankingCcvUnitario.slice(0, 5).map(r => ({
                  nombre: r.nombre,
                  valor: `${fmtEur(r.ccv_por_kw)}/kW`,
                }))} />
            )}
            {rankingCcvTotal.length > 0 && (
              <RankingBlock
                title="Mayor ahorro económico absoluto"
                subtitle="CCV total · más negativo = más ahorro total"
                items={rankingCcvTotal.slice(0, 5).map(r => ({
                  nombre: r.nombre,
                  valor: fmtEur(r.ccv_total_eur),
                }))} />
            )}
            {rankingGwpUnitario.length > 0 && (
              <RankingBlock
                title="Menor huella de carbono unitaria"
                subtitle="kg CO₂/kW embebido · menor = mejor"
                items={rankingGwpUnitario.slice(0, 5).map(r => ({
                  nombre: r.nombre,
                  valor: `${Math.round(Number(r.gwp_a1_a3_kg_co2_por_kw))} kg CO₂/kW`,
                }))} />
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
        <button style={BTN_G} onClick={onBack}>
          <ArrowLeft size={16} weight="regular" /> Atrás
        </button>
        <button style={BTN_P} onClick={onNext}>
          Continuar a la firma <ArrowRight size={16} weight="regular" />
        </button>
      </div>
    </div>
  )
}

// Sub-componente · caja de ranking (top 5)
function RankingBlock({ title, subtitle, items }) {
  return (
    <div style={{ background: C.cream, border: `1px solid ${C.border}`, borderRadius: '5px', padding: '0.75rem 0.9rem' }}>
      <div style={{ fontSize: '0.78rem', color: C.dark, fontWeight: 700, marginBottom: '2px' }}>{title}</div>
      <div style={{ fontSize: '0.68rem', color: C.muted, marginBottom: '0.5rem' }}>{subtitle}</div>
      <ol style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.78rem', color: C.dark }}>
        {items.map((it, i) => (
          <li key={i} style={{ marginBottom: '0.2rem', color: i === 0 ? C.olive : C.dark, fontWeight: i === 0 ? 700 : 400 }}>
            {it.nombre} <span style={{ color: C.muted, fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.72rem' }}>· {it.valor}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
//        PANTALLA 4 · Recomendación final + firma externa (opcional)
// ────────────────────────────────────────────────────────────────────────────
// El flujo real de HAUS: Gisell prepara el informe, se lo pasa en PDF al
// firmante técnico externo (Juan Felipe Silva o quien corresponda), este
// lo firma fuera de la app y devuelve el PDF firmado. Gisell sube ese PDF
// a la app como evidencia y el estudio queda cerrado.
function Step4Sign({ study, catalog, selectedTechs, onSave, onBack, flash }) {
  const [firmanteNombre, setFirmanteNombre] = useState(study.firmante_nombre || '')
  const [firmanteTitulacion, setFirmanteTitulacion] = useState(study.firmante_titulacion || '')
  const [firmanteColegiado, setFirmanteColegiado] = useState(study.firmante_colegiado || '')
  const [firmanteEmail, setFirmanteEmail] = useState(study.firmante_email || '')
  const [fechaFirma, setFechaFirma] = useState(study.fecha_firma_estudio || new Date().toISOString().slice(0,10))
  const [tecnologiaRec, setTecnologiaRec] = useState(study.tecnologia_recomendada_id || '')
  const [conclusion, setConclusion] = useState(study.conclusion_texto || '')
  const [pdfPath, setPdfPath] = useState(study.storage_path_estudio_firmado || null)
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)

  const tecOptions = catalog.filter(t => selectedTechs[t.id])
  const yaFirmado = study.estado === 'firmado'

  const guardarBorrador = async () => {
    await onSave({
      firmante_user_id: null,
      firmante_nombre: firmanteNombre || null,
      firmante_titulacion: firmanteTitulacion || null,
      firmante_colegiado: firmanteColegiado || null,
      firmante_email: firmanteEmail || null,
      fecha_firma_estudio: fechaFirma || null,
      tecnologia_recomendada_id: tecnologiaRec ? Number(tecnologiaRec) : null,
      conclusion_texto: conclusion || null,
      storage_path_estudio_firmado: pdfPath,
    })
    flash('Datos guardados', 'ok')
  }

  const subirPdfFirmado = async (file) => {
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      flash('El archivo debe ser un PDF', 'err'); return
    }
    setUploading(true)
    try {
      const path = `boc_studies/${study.id}/estudio_firmado_${Date.now()}.pdf`
      const { error } = await supabase.storage.from('project-documents')
        .upload(path, file, { contentType: 'application/pdf', upsert: true })
      if (error) throw error
      setPdfPath(path)
      await supabase.from('boc_studies')
        .update({ storage_path_estudio_firmado: path })
        .eq('id', study.id)
      flash('PDF firmado subido correctamente', 'ok')
    } catch (e) {
      flash(`Error al subir el PDF: ${e.message}`, 'err')
    } finally {
      setUploading(false)
    }
  }

  const cerrarComoFirmado = async () => {
    if (!firmanteNombre.trim()) { flash('Falta el nombre del firmante', 'err'); return }
    if (!fechaFirma) { flash('Falta la fecha de firma', 'err'); return }
    if (!tecnologiaRec) { flash('Selecciona la tecnología recomendada', 'err'); return }

    setSaving(true)
    try {
      const { error } = await supabase.from('boc_studies').update({
        firmante_user_id: null,
        firmante_nombre: firmanteNombre.trim(),
        firmante_titulacion: firmanteTitulacion || null,
        firmante_colegiado: firmanteColegiado || null,
        firmante_email: firmanteEmail || null,
        fecha_firma_estudio: fechaFirma,
        tecnologia_recomendada_id: Number(tecnologiaRec),
        conclusion_texto: conclusion || null,
        storage_path_estudio_firmado: pdfPath,
        estado: 'firmado',
      }).eq('id', study.id)
      if (error) throw error
      flash('Estudio marcado como firmado. Puedes descargar el docx en la próxima fase.', 'ok')
    } catch (e) {
      flash(`Error: ${e.message}`, 'err')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {yaFirmado && (
        <div style={{ padding: '0.7rem 1rem', background: '#F1F5E8',
                      border: `1px solid ${C.ok}`, borderRadius: '5px',
                      color: C.dark, fontSize: '0.85rem',
                      display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <CheckCircle size={20} weight="regular" style={{ color: C.ok }} />
          Estudio cerrado como firmado el {fechaFirma} por {study.firmante_nombre}. Solo consulta.
        </div>
      )}

      {/* Datos del firmante externo */}
      <div style={CARD}>
        <h3 style={SECTION_H}>
          <Signature size={22} weight="regular" style={{ color: C.olive }} />
          Firmante técnico
        </h3>
        <p style={{ fontSize: '0.85rem', color: C.muted, marginTop: 0 }}>
          El estudio B0C debe firmarlo un especialista en energía independiente del asesor BREEAM.
          Introduce sus datos aquí. La firma real la hará él/ella fuera de la app · después subes el PDF firmado como evidencia.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '1rem' }}>
          <div>
            <label style={LABEL}>Nombre completo</label>
            <input type="text" value={firmanteNombre} onChange={e => setFirmanteNombre(e.target.value)}
              disabled={yaFirmado}
              style={{ ...INP, ...(yaFirmado ? INP_RO : {}) }}
              placeholder="Ej. Juan Felipe Silva" />
          </div>
          <div>
            <label style={LABEL}>Titulación</label>
            <input type="text" value={firmanteTitulacion} onChange={e => setFirmanteTitulacion(e.target.value)}
              disabled={yaFirmado}
              style={{ ...INP, ...(yaFirmado ? INP_RO : {}) }}
              placeholder="Ej. Ingeniero industrial · máster energías renovables" />
          </div>
          <div>
            <label style={LABEL}>Nº colegiado / colegio</label>
            <input type="text" value={firmanteColegiado} onChange={e => setFirmanteColegiado(e.target.value)}
              disabled={yaFirmado}
              style={{ ...INP, ...(yaFirmado ? INP_RO : {}) }}
              placeholder="Ej. 12345 · COIICV" />
          </div>
          <div>
            <label style={LABEL}>Email</label>
            <input type="email" value={firmanteEmail} onChange={e => setFirmanteEmail(e.target.value)}
              disabled={yaFirmado}
              style={{ ...INP, ...(yaFirmado ? INP_RO : {}) }}
              placeholder="firmante@haus-hb.com" />
          </div>
        </div>
      </div>

      {/* Recomendación */}
      <div style={CARD}>
        <h3 style={SECTION_H}>
          <PencilSimple size={22} weight="regular" style={{ color: C.olive }} />
          Recomendación final del estudio
        </h3>
        <div style={{ marginBottom: '1rem' }}>
          <label style={LABEL}>Tecnología recomendada</label>
          <select value={tecnologiaRec} onChange={e => setTecnologiaRec(e.target.value)}
            style={{ ...INP, ...(yaFirmado ? INP_RO : {}) }} disabled={yaFirmado}>
            <option value="">— seleccionar —</option>
            {tecOptions.map(t => (
              <option key={t.id} value={t.id}>{t.nombre}</option>
            ))}
          </select>
        </div>
        <label style={LABEL}>Justificación técnica</label>
        <textarea rows={5} value={conclusion} onChange={e => setConclusion(e.target.value)}
          disabled={yaFirmado}
          style={{ ...INP, resize: 'vertical', minHeight: '100px' }}
          placeholder="Razones por las que se recomienda esta tecnología frente a las alternativas evaluadas..." />
      </div>

      {/* Fecha */}
      <div style={CARD}>
        <h3 style={SECTION_H}>
          <Files size={22} weight="regular" style={{ color: C.olive }} />
          Fecha de firma prevista o realizada
        </h3>
        <input type="date" value={fechaFirma} onChange={e => setFechaFirma(e.target.value)}
          disabled={yaFirmado}
          style={{ ...INP, ...(yaFirmado ? INP_RO : {}), maxWidth: '250px' }} />
      </div>

      {/* Upload PDF firmado */}
      <div style={CARD}>
        <h3 style={SECTION_H}>
          <Files size={22} weight="regular" style={{ color: C.olive }} />
          PDF del estudio firmado (evidencia)
        </h3>
        <p style={{ fontSize: '0.82rem', color: C.muted, marginTop: 0 }}>
          Opcional en el borrador. Recomendado antes de cerrar el estudio como firmado.
          El auditor BREEAM pedirá el PDF firmado como evidencia de ENE 4.
        </p>
        {pdfPath && (
          <div style={{ padding: '0.6rem 0.9rem', background: '#F1F5E8',
                        border: `1px solid ${C.ok}`, borderRadius: '5px',
                        color: C.dark, fontSize: '0.82rem',
                        display: 'flex', gap: '0.5rem', alignItems: 'center',
                        marginBottom: '0.8rem' }}>
            <CheckCircle size={16} weight="regular" style={{ color: C.ok }} />
            PDF cargado · <code style={{ fontSize: '0.75rem', color: C.muted }}>{pdfPath.split('/').pop()}</code>
          </div>
        )}
        <label style={{ ...BTN_G, cursor: uploading ? 'wait' : 'pointer', display: 'inline-flex' }}>
          {uploading ? <Spinner size={16} weight="regular" /> : <Files size={16} weight="regular" />}
          {pdfPath ? 'Sustituir PDF firmado' : 'Subir PDF firmado'}
          <input type="file" accept=".pdf,application/pdf"
            onChange={e => e.target.files?.[0] && subirPdfFirmado(e.target.files[0])}
            disabled={uploading || yaFirmado}
            style={{ display: 'none' }} />
        </label>
      </div>

      {/* Navegación */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
        <button style={BTN_G} onClick={onBack}>
          <ArrowLeft size={16} weight="regular" /> Atrás
        </button>
        {!yaFirmado ? (
          <>
            <button style={BTN_G} onClick={guardarBorrador}>
              <FloppyDisk size={16} weight="regular" /> Guardar borrador
            </button>
            <button style={BTN_P} onClick={cerrarComoFirmado} disabled={saving}>
              {saving ? <Spinner size={16} weight="regular" /> : <CheckCircle size={16} weight="regular" />}
              Marcar como firmado
            </button>
          </>
        ) : (
          <button style={BTN_G} disabled>
            <CheckCircle size={16} weight="regular" /> Estudio ya cerrado
          </button>
        )}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
//                              HELPERS UI
// ────────────────────────────────────────────────────────────────────────────
// Celda de vista múltiple · muestra el kg CO₂ total (grande) + kg/kW y kg/m² debajo
function CellMultiVista({ total, porKw, area }) {
  if (!total && !porKw) return <span style={{ color: '#7A6A5A' }}>—</span>
  const porM2 = area && total ? total / area : null
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
        {total ? Number(total).toLocaleString('es-ES', { maximumFractionDigits: 0 }) : '—'}
      </div>
      <div style={{ fontSize: '0.65rem', color: '#7A6A5A', fontFamily: "'IBM Plex Mono', monospace" }}>
        {porKw ? `${Math.round(porKw)} /kW` : ''}
        {porKw && porM2 ? ' · ' : ''}
        {porM2 ? `${Math.round(porM2)} /m²` : ''}
      </div>
    </div>
  )
}

// Celda editable · input para el valor TOTAL + resumen debajo con kg/kW y kg/m²
function CellEditableMultiVista({ total, porKw, area, editableValue, onChange, placeholder, placeholderHint, isFallback }) {
  // Si hay editableValue (usado en EPD real cuando el asesor ha introducido su valor)
  // usarlo como controlled input · si no, usar el total calculado
  // Redondeamos a 0 decimales para que el input no muestre 18699,725560707862
  const rawValue = editableValue != null ? editableValue : (total != null ? total : '')
  const displayValue = rawValue !== '' ? Math.round(Number(rawValue)) : ''
  const porM2 = area && (editableValue != null ? editableValue : total) ? (editableValue != null ? editableValue : total) / area : null
  return (
    <div style={{ textAlign: 'center' }}>
      <input type="number"
        value={displayValue === '' ? '' : displayValue}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder != null ? placeholder : (placeholderHint ? `≈ ${placeholderHint.toLocaleString('es-ES')}` : '')}
        title={placeholderHint ? `Estimación del catálogo: ~${placeholderHint.toLocaleString('es-ES')} kg CO₂ total · introduce el valor real de la EPD del fabricante para mayor precisión` : ''}
        style={{ padding: '0.2rem 0.3rem', border: '1px solid #DDD5C8', borderRadius: '4px',
                 background: '#fff', textAlign: 'center', fontSize: '0.85rem', fontWeight: 700,
                 width: '100%', boxSizing: 'border-box',
                 fontStyle: isFallback && !editableValue ? 'italic' : 'normal',
                 color: isFallback && !editableValue ? '#7A6A5A' : '#3B2F2F' }} />
      <div style={{ fontSize: '0.65rem', color: '#7A6A5A', fontFamily: "'IBM Plex Mono', monospace", marginTop: '2px' }}>
        {porKw ? `${Math.round(porKw)} /kW` : ''}
        {porKw && porM2 ? ' · ' : ''}
        {porM2 ? `${Math.round(porM2)} /m²` : ''}
      </div>
    </div>
  )
}

// Variante compacta para bloques de datos densos (Pantalla 1 unificada)
function FieldCompact({ label, value, small }) {
  return (
    <div>
      <div style={{ fontSize: '0.65rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.02em', fontWeight: 600, marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '0.82rem', color: C.dark, lineHeight: '1.2' }}>{value}</div>
      {small && <div style={{ fontSize: '0.67rem', color: C.muted, marginTop: '1px' }}>{small}</div>}
    </div>
  )
}
function fmtEur(v) {
  if (v == null || v === '') return '—'
  return Number(v).toLocaleString('es-ES', { maximumFractionDigits: 0 }) + ' €'
}
// ─── Formato de números para el ACV completo ────────────────────────────────
// Los valores del ACV cubren 6 órdenes de magnitud (kg CO₂ eq va de miles a
// microgramos, ODP va de 10⁻⁸ a 10⁻⁵, etc.). Combinamos notación estándar
// y científica según la magnitud absoluta.
function fmtNum(v) {
  if (v == null) return '—'
  const abs = Math.abs(v)
  if (abs === 0) return '0'
  if (abs < 0.001) return v.toExponential(2).replace('.', ',')
  if (abs < 1) return v.toFixed(3).replace('.', ',')
  if (abs < 100) return v.toFixed(2).replace('.', ',')
  return v.toLocaleString('es-ES', { maximumFractionDigits: 0 })
}

// ─── Badge de fuente · verde/ámbar/rojo según jerarquía + BD ────────────────
function FuenteBadge({ cobertura }) {
  if (!cobertura) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                     padding: '0.15rem 0.5rem', borderRadius: '3px',
                     background: '#F8E4E0', border: `1px solid ${C.coral}`,
                     fontSize: '0.7rem', color: C.coral, fontWeight: 600 }}>
        <Warning size={12} weight="regular" /> Sin datos ACV
      </span>
    )
  }
  const { tiene_dap_subida, tiene_dap_bd_publica, fuente_bd_principal,
          fuente_principal, jerarquia_principal } = cobertura
  if (tiene_dap_subida) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                     padding: '0.15rem 0.5rem', borderRadius: '3px',
                     background: '#E0F0DC', border: `1px solid ${C.ok}`,
                     fontSize: '0.7rem', color: C.ok, fontWeight: 600 }}>
        <CheckCircle size={12} weight="regular" /> DAP del proyecto
      </span>
    )
  }
  if (tiene_dap_bd_publica) {
    const isVerificada = jerarquia_principal && jerarquia_principal <= 2
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                     padding: '0.15rem 0.5rem', borderRadius: '3px',
                     background: isVerificada ? '#E0F0DC' : '#FFF5E0',
                     border: `1px solid ${isVerificada ? C.ok : C.amber}`,
                     fontSize: '0.7rem',
                     color: isVerificada ? C.ok : C.amber, fontWeight: 600 }}
            title={fuente_principal ? `Fuente: ${fuente_principal}` : ''}>
        <CheckCircle size={12} weight="regular" />
        DAP BD pública {fuente_bd_principal ? `· ${fuente_bd_principal}` : ''}
      </span>
    )
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                   padding: '0.15rem 0.5rem', borderRadius: '3px',
                   background: '#F8E4E0', border: `1px solid ${C.coral}`,
                   fontSize: '0.7rem', color: C.coral, fontWeight: 600 }}>
      <Warning size={12} weight="regular" /> Sin EPD · solicitar al fabricante
    </span>
  )
}

// ─── Bloque · ACV completo (13 categorías × 15 fases por tecnología) ───────
function AcvCompletoBloque({ study, selectedTechs, siteData, catalog,
                             acvResumen, acvMatriz, acvCobertura,
                             expandedTech, computing, onRecalcular,
                             onToggleExpand, flash }) {
  // Estado del modal "Fuente ACV" · guarda la tecnología abierta
  // IMPORTANTE · los hooks deben ejecutarse SIEMPRE en el mismo orden en
  // cada render (rules-of-hooks). El early return va DESPUÉS de los hooks.
  const [modalTech, setModalTech] = useState(null)
  const [showMetodologia, setShowMetodologia] = useState(false)

  // Consolidar la lista de tecnologías del estudio: viene de acvCobertura
  // (que devuelve TODAS las tecs del estudio, tengan o no EPD cargada) y se
  // enriquece con las filas del resumen ACV cuando existen.
  const tecnologias = useMemo(() => {
    // 1. Cargar todas las tecnologías del estudio desde cobertura
    const map = new Map()
    for (const c of acvCobertura) {
      map.set(c.technology_id, {
        technology_id: c.technology_id,
        tech_key: c.tech_key,
        tech_nombre: c.tech_nombre,
        potencia_kw: null,
        area_m2: null,
        categorias: [],
        cobertura: c,
      })
    }
    // 2. Enriquecer con datos ACV cuando existan
    for (const row of acvResumen) {
      if (!map.has(row.technology_id)) {
        map.set(row.technology_id, {
          technology_id: row.technology_id,
          tech_key: row.tech_key,
          tech_nombre: row.tech_nombre,
          potencia_kw: row.potencia_kw,
          area_m2: row.area_m2,
          categorias: [],
          cobertura: null,
        })
      }
      const t = map.get(row.technology_id)
      t.potencia_kw = t.potencia_kw ?? row.potencia_kw
      t.area_m2 = t.area_m2 ?? row.area_m2
      t.categorias.push(row)
    }
    return Array.from(map.values())
  }, [acvResumen, acvCobertura])

  if (!study?.id) return null

  return (
    <div style={CARD}>
      <h3 style={SECTION_H}>
        <Leaf size={22} weight="regular" style={{ color: C.olive }} />
        ACV completo · UNE-EN ISO 14044:2006
        <button
          onClick={() => setShowMetodologia(true)}
          title="Cómo se calcula el ACV embebido · metodología, fuentes y aviso"
          aria-label="Información sobre la metodología del ACV"
          style={{ background: 'transparent', border: `1px solid ${C.border}`,
                   borderRadius: '999px', width: '22px', height: '22px',
                   display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                   cursor: 'pointer', color: C.muted, marginLeft: '0.35rem',
                   fontSize: '0.8rem', fontWeight: 700, padding: 0 }}>
          <Info size={14} weight="regular" />
        </button>
        <button style={{ ...BTN_G, marginLeft: 'auto' }}
                onClick={onRecalcular} disabled={computing}>
          {computing
            ? <Spinner size={14} weight="regular" />
            : <Calculator size={14} weight="regular" />}
          Recalcular ACV completo
        </button>
      </h3>
      <p style={{ fontSize: '0.82rem', color: C.muted, marginTop: 0, marginBottom: '0.9rem' }}>
        <strong>13 categorías de impacto</strong> (EN 15804+A2:2019) × <strong>15 fases del ciclo de vida</strong> (EN 15978)
        por cada tecnología. Motor propio HAUS con trazabilidad completa de fuente
        · cuando existe EPD verificada (jerarquía 1-2) el badge sale en verde;
        cuando se usa un genérico ÖKOBAUDAT/Ecoinvent (jerarquía 3-4) sale en ámbar.
        <br />
        Pulsa <strong>Cambiar fuente ACV</strong> en cualquier tecnología para subir la DAP
        del fabricante concreto del proyecto, buscar en las 5 BBDD públicas (ÖKOBAUDAT · IBU
        · Environdec · DAPcons · OpenDAP) o dejar constancia de que no hay EPD disponible.
      </p>

      {tecnologias.length === 0 && !computing && (
        <div style={{ padding: '1rem', background: C.cream, border: `1px solid ${C.border}`,
                      borderRadius: '5px', fontSize: '0.85rem', color: C.muted }}>
          No hay tecnologías en el estudio. Añade tecnologías en la Pantalla 2 y vuelve a
          esta pantalla.
        </div>
      )}

      {tecnologias.map(tech => {
        const cobertura = tech.cobertura
        const tieneResumen = tech.categorias.length > 0
        const isExpanded = expandedTech === tech.technology_id
        const matrixRows = acvMatriz.filter(m => m.technology_id === tech.technology_id)

        return (
          <div key={tech.technology_id}
               style={{ background: C.cream, border: `1px solid ${C.border}`,
                        borderRadius: '5px', padding: '0.9rem', marginBottom: '0.9rem' }}>

            {/* Cabecera · nombre + potencia + badge fuente + botón cambiar fuente */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem',
                          marginBottom: '0.65rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: C.dark }}>
                  {tech.tech_nombre}
                </div>
                <div style={{ fontSize: '0.72rem', color: C.muted,
                              fontFamily: "'IBM Plex Mono', monospace" }}>
                  {tech.potencia_kw ? `${tech.potencia_kw} kWp` : '— kWp'}
                  {tech.area_m2 ? ` · ${tech.area_m2} m²` : ''}
                </div>
              </div>
              <FuenteBadge cobertura={cobertura} />
              <button style={BTN_G}
                      onClick={() => setModalTech(tech)}
                      title="Subir DAP, buscar en BBDD públicas o justificar sin EPD">
                <Files size={14} weight="regular" />
                Cambiar fuente ACV
              </button>
            </div>

            {tieneResumen ? (
              <>
                {/* Tabla resumen · 13 categorías × 3 totales (A1-C4, D, A1-D) */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '0.76rem',
                                  borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...thStyle, textAlign: 'left' }}>Categoría de impacto</th>
                        <th style={thStyle}>A1-C4 · ciclo</th>
                        <th style={thStyle}>D · créditos</th>
                        <th style={thStyle}>A1-D · neto</th>
                        <th style={thStyle}>Unidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tech.categorias.map(cat => (
                        <tr key={cat.categoria_codigo}>
                          <td style={{ ...tdStyle, textAlign: 'left' }}>
                            {cat.categoria_nombre}
                          </td>
                          <td style={tdStyle}>{fmtNum(cat.total_a1_c4)}</td>
                          <td style={{ ...tdStyle,
                                       color: cat.total_d < 0 ? C.ok : C.dark }}>
                            {fmtNum(cat.total_d)}
                          </td>
                          <td style={{ ...tdStyle, fontWeight: 700 }}>
                            {fmtNum(cat.total_a1_d)}
                          </td>
                          <td style={{ ...tdStyle, color: C.muted, fontSize: '0.72rem' }}>
                            {cat.categoria_unidad}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Bloque · 2 escenarios D (solo material vs con electricidad
                    exportada) para tecnologías renovables eléctricas con
                    producción > 0. Coherente con el docx generado (Fase 2.8).
                    La decisión de qué escenario usar queda al firmante. */}
                {(() => {
                  const cat = (catalog || []).find(
                    c => c.id === tech.technology_id
                  ) || {}
                  const stech = (selectedTechs || {})[tech.technology_id] || {}
                  const grupo = cat.grupo || ''
                  const tipo = cat.tipo_energia || ''
                  const produccion = Number(stech.produccion) || 0
                  const factorIdae = Number(
                    siteData?.factor_emision_electricidad_kg_kwh
                  ) || 0
                  const esExportadora = (
                    (tipo === 'electrica' || grupo === 'renovable_electrica')
                    && produccion > 0 && factorIdae > 0
                  )
                  if (!esExportadora) return null
                  const gwp = tech.categorias.find(
                    c => c.categoria_codigo === 'GWP-total'
                  )
                  if (!gwp) return null
                  const a1c4 = Number(gwp.total_a1_c4) || 0
                  const dMat = Number(gwp.total_d) || 0
                  const a1dMat = Number(gwp.total_a1_d) || 0
                  // Vida útil por defecto 30 años para FV, 15 para térmicas
                  const vidaUtil = grupo === 'renovable_electrica' ? 30 : 15
                  const dElec = -1 * produccion * vidaUtil * factorIdae
                  const dTotal = dMat + dElec
                  const a1dTotal = a1c4 + dTotal
                  return (
                    <div style={{ marginTop: '0.7rem', padding: '0.65rem 0.85rem',
                                  background: '#EEF6F0',
                                  border: `1px solid ${C.ok}`,
                                  borderRadius: '4px',
                                  fontSize: '0.76rem' }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 700,
                                    color: C.ok, marginBottom: '0.35rem' }}>
                        Módulo D · GWP-total · 2 escenarios
                      </div>
                      <div style={{ display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '0.55rem' }}>
                        <div>
                          <div style={{ fontWeight: 600, color: C.dark }}>
                            Escenario 1 · solo reciclaje panel (EPD)
                          </div>
                          <div style={{ color: C.muted, marginTop: '0.15rem' }}>
                            D-material · {fmtNum(dMat)} kg CO₂ eq
                          </div>
                          <div style={{ color: C.dark, fontWeight: 700,
                                        marginTop: '0.1rem' }}>
                            A1-D neto · {fmtNum(a1dMat)} kg CO₂ eq
                          </div>
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: C.dark }}>
                            Escenario 2 · reciclaje + electricidad exportada
                          </div>
                          <div style={{ color: C.muted, marginTop: '0.15rem' }}>
                            D-total · {fmtNum(dTotal)} kg CO₂ eq
                          </div>
                          <div style={{ color: C.dark, fontWeight: 700,
                                        marginTop: '0.1rem' }}>
                            A1-D neto · {fmtNum(a1dTotal)} kg CO₂ eq
                          </div>
                        </div>
                      </div>
                      <div style={{ marginTop: '0.4rem',
                                    fontSize: '0.7rem', color: C.muted,
                                    fontFamily: "'IBM Plex Mono', monospace" }}>
                        Cálculo D-electricidad · −{Math.round(produccion).toLocaleString('es-ES')} kWh/año
                        × {vidaUtil} años × {factorIdae.toFixed(3).replace('.', ',')} kg CO₂/kWh IDAE
                        = {fmtNum(dElec)} kg CO₂ eq
                      </div>
                      <div style={{ marginTop: '0.35rem', fontSize: '0.7rem',
                                    color: C.muted, fontStyle: 'italic' }}>
                        La EPD solo declara D-material · el D-electricidad
                        es cálculo propio del motor HAUS con factor IDAE local.
                        La decisión queda al firmante especialista.
                      </div>
                    </div>
                  )
                })()}

                {/* Botón expandir · matriz 13×15 */}
                <div style={{ marginTop: '0.6rem' }}>
                  <button style={BTN_G}
                          onClick={() => onToggleExpand(tech.technology_id)}>
                    {isExpanded
                      ? <>Ocultar matriz 13×15</>
                      : <>Ver matriz completa · 13 categorías × 15 fases</>}
                  </button>
                </div>

                {isExpanded && (
                  <MatrizAcvTabla rows={matrixRows} techNombre={tech.tech_nombre} />
                )}
              </>
            ) : (
              <div style={{ padding: '0.6rem 0.9rem', background: '#fff',
                            border: `1px dashed ${C.border}`, borderRadius: '4px',
                            fontSize: '0.78rem', color: C.muted }}>
                Sin datos ACV para esta tecnología. Pulsa <strong>Cambiar fuente ACV</strong>
                para subir la DAP del fabricante, buscar en las 5 BBDD públicas o justificar
                por qué no hay EPD disponible.
              </div>
            )}
          </div>
        )
      })}

      {/* Modal · Fuente ACV */}
      {modalTech && (
        <EpdFuenteModal
          study={study}
          tecnologia={modalTech}
          flash={flash}
          onClose={() => setModalTech(null)}
          onSaved={() => {
            setModalTech(null)
            onRecalcular()
          }}
        />
      )}

      {/* Modal · Metodología del ACV embebido (disparado desde el icono 'i') */}
      {showMetodologia && (
        <MetodologiaAcvModal onClose={() => setShowMetodologia(false)} />
      )}
    </div>
  )
}

// ─── Modal · metodología del ACV embebido ──────────────────────────────────
// Antes vivía como bloque desplegado bajo la tabla ACV. Ahora es un modal
// que se abre solo cuando Gisell pulsa el icono 'i' del título del bloque
// ACV completo · para no ensuciar la vista cuando no se necesita.
function MetodologiaAcvModal({ onClose }) {
  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(59,47,47,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '1rem',
  }
  const modalStyle = {
    background: C.card, borderRadius: '8px', maxWidth: '780px', width: '100%',
    maxHeight: '90vh', overflow: 'auto', boxShadow: '0 6px 24px rgba(0,0,0,0.2)',
  }
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: `1px solid ${C.border}`,
                      display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Info size={20} weight="regular" style={{ color: C.olive }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.7rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Metodología
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: C.dark }}>
              Cómo se calcula el ACV embebido
            </div>
          </div>
          <button onClick={onClose}
                  style={{ background: 'transparent', border: 'none', fontSize: '1.4rem',
                           color: C.muted, cursor: 'pointer', padding: '0.2rem 0.5rem' }}>
            ✕
          </button>
        </div>
        <div style={{ padding: '1.25rem', fontSize: '0.82rem', color: C.dark, lineHeight: 1.6 }}>
          <p style={{ margin: '0 0 0.6rem' }}>
            Método <strong>UNE-EN 15978</strong> · Sostenibilidad en la construcción · Evaluación
            del comportamiento ambiental de edificios. Solo cubre las <strong>fases A1-A3</strong>
            (extracción de materias primas, transporte a fábrica, fabricación).
            La app <strong>no calcula</strong> A4-A5 (transporte a obra + puesta en obra),
            B1-B7 (uso), C1-C4 (fin de vida) ni D (reciclaje) · esas fases requerirían datos
            específicos del proyecto y quedan fuera del alcance del estudio B0C.
          </p>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.78rem',
                        background: C.cream, padding: '0.5rem 0.75rem', borderRadius: '4px',
                        border: `1px solid ${C.border}`, margin: '0.4rem 0 0.9rem',
                        color: C.brown }}>
            GWP embebido = GWP unitario (kg CO₂eq/kW) × potencia instalada (kW)
          </div>
          <p style={{ margin: '0 0 0.4rem' }}>
            <strong>Fuentes de datos</strong> · en orden de preferencia:
          </p>
          <ol style={{ margin: '0 0 0.9rem', paddingLeft: '1.2rem' }}>
            <li><strong>EPD Environdec verificada del fabricante</strong> (cuando el proyecto especifica marca-modelo concretos) · lo más preciso</li>
            <li><strong>ÖKOBAUDAT 2024-I</strong> · base de datos alemana oficial · datasets genéricos por tipo de tecnología · valor medio del rango publicado</li>
            <li><strong>Ecoinvent 3.9</strong> · para tecnologías no cubiertas por ÖKOBAUDAT (ej. CHP, algunos módulos FV específicos)</li>
            <li><strong>Literatura técnica IDAE / IRENA</strong> · fallback cuando no hay EPDs disponibles</li>
          </ol>
          <p style={{ margin: '0 0 0.4rem' }}><strong>Cada columna</strong>:</p>
          <ul style={{ margin: '0 0 0.9rem', paddingLeft: '1.2rem' }}>
            <li><strong>GWP unitario (kg CO₂/kW)</strong> · valor medio del rango del catálogo para la tecnología</li>
            <li><strong>Rango incertidumbre</strong> · extremos min-max típicos del sector según fuentes consultadas · refleja que dos EPDs de fabricantes distintos pueden diferir hasta ±25 %</li>
            <li><strong>GWP total (kg CO₂eq)</strong> · valor unitario × potencia · magnitud absoluta</li>
            <li><strong>Método</strong> · qué fuente concreta se usó (ÖKOBAUDAT genérico, EPD Environdec, literatura, etc.)</li>
          </ul>
          <p style={{ margin: '0 0 0.9rem', padding: '0.5rem 0.75rem',
                      background: '#FFF5E0', border: `1px solid ${C.amber}`,
                      borderRadius: '4px', fontSize: '0.78rem' }}>
            <strong>Aviso metodológico</strong> · este ACV es <strong>orientativo</strong>.
            Para MAT 1 de BREEAM se exige una herramienta reconocida (One Click LCA, LCAbyg, etc.).
            Nuestro motor sirve para dimensionar el orden de magnitud del carbono embebido y
            para comparar tecnologías entre sí · no sustituye un ACV formal certificable.
            Discrepancias típicas con One Click LCA: <strong>±10-25 %</strong> por uso de BDs distintas
            (ÖKOBAUDAT vs Ecoinvent), boundary conditions y factores de asignación.
          </p>
          <p style={{ margin: '0' }}>
            <strong>Trazabilidad</strong> · cada valor tiene guardada su fuente exacta en la base
            de datos (documento, URL, fecha de consulta, unit process usado). El anexo de
            trazabilidad del documento firmado incluirá estos detalles para auditar cualquier cifra.
          </p>
        </div>
      </div>
    </div>
  )
}

// ─── Tabla pivot · 13 categorías (filas) × N fases (cols) ───────────────────
function MatrizAcvTabla({ rows, techNombre }) {
  if (!rows || rows.length === 0) {
    return (
      <div style={{ padding: '0.75rem', color: C.muted, fontSize: '0.8rem' }}>
        Cargando la matriz…
      </div>
    )
  }
  // Ejes únicos ordenados
  const fases = Array.from(new Map(
    rows.map(r => [r.fase_codigo, { codigo: r.fase_codigo, nombre: r.fase_nombre, orden: r.fase_orden }])
  ).values()).sort((a, b) => a.orden - b.orden)

  const categorias = Array.from(new Map(
    rows.map(r => [r.categoria_codigo, {
      codigo: r.categoria_codigo, nombre: r.categoria_nombre,
      unidad: r.categoria_unidad, orden: r.categoria_orden,
    }])
  ).values()).sort((a, b) => a.orden - b.orden)

  const valor = (cat, fase) => {
    const row = rows.find(r => r.categoria_codigo === cat && r.fase_codigo === fase)
    return row ? row.valor_total : null
  }

  return (
    <div style={{ marginTop: '0.65rem', padding: '0.75rem',
                  background: '#fff', border: `1px solid ${C.border}`,
                  borderRadius: '5px' }}>
      <div style={{ fontSize: '0.78rem', color: C.dark, fontWeight: 600, marginBottom: '0.5rem' }}>
        Matriz completa · {techNombre}
        <span style={{ color: C.muted, fontWeight: 400, marginLeft: '0.5rem' }}>
          · {categorias.length} categorías × {fases.length} fases
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ fontSize: '0.72rem', borderCollapse: 'collapse',
                        minWidth: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: 'left', position: 'sticky',
                           left: 0, background: '#fff', zIndex: 1 }}>Categoría</th>
              {fases.map(f => (
                <th key={f.codigo} style={{ ...thStyle, whiteSpace: 'nowrap' }}
                    title={f.nombre}>
                  {f.codigo}
                </th>
              ))}
              <th style={{ ...thStyle, whiteSpace: 'nowrap' }}>Unidad</th>
            </tr>
          </thead>
          <tbody>
            {categorias.map(cat => (
              <tr key={cat.codigo}>
                <td style={{ ...tdStyle, textAlign: 'left', position: 'sticky',
                             left: 0, background: '#fff', zIndex: 1,
                             fontWeight: 500, whiteSpace: 'nowrap' }}
                    title={cat.nombre}>
                  {cat.codigo}
                </td>
                {fases.map(f => {
                  const v = valor(cat.codigo, f.codigo)
                  return (
                    <td key={f.codigo} style={{ ...tdStyle,
                                                fontSize: '0.7rem',
                                                color: v == null
                                                  ? C.muted
                                                  : (v < 0 ? C.ok : C.dark) }}>
                      {fmtNum(v)}
                    </td>
                  )
                })}
                <td style={{ ...tdStyle, color: C.muted,
                             fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                  {cat.unidad}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: C.muted,
                    fontStyle: 'italic' }}>
        Valores en color oliva son negativos · corresponden a los créditos
        del módulo D (reciclaje, reutilización). Las celdas vacías indican
        fases que la EPD declara con impacto 0 (ej. B1-B7 en módulos FV
        self-cleaning · C1 deconstrucción manual sin energía).
      </div>
    </div>
  )
}

// ─── Explicación dinámica de por qué difieren los 3 escenarios ─────────────
// Calcula las desviaciones reales del estudio y las presenta en texto.
// Antes teníamos un texto fijo con ejemplos de FV (JA Solar, Jinko, PERC/TOPCon)
// que no aplicaba a otras tecnologías (aerotermia, biomasa, geotermia, ...).
// Ahora el bloque es genérico + añade la tabla real de este estudio concreto.
function ExplicacionEscenariosDinamica({ acvEnriched }) {
  // Sólo tecnologías con al menos 2 escenarios rellenos · si no, no hay
  // desviaciones que comparar
  const filas = acvEnriched
    .map(r => {
      const gen = Number(r.gwp_generico_kw) || null
      const one = Number(r.gwp_oneclick_kw) || null
      const epd = Number(r.gwp_epd_kw) || null
      const desv = (a, b) => (a != null && b != null && b !== 0
        ? ((a - b) / b) * 100 : null)
      return {
        nombre: r.nombre,
        pot: r.potencia_kw,
        gen, one, epd,
        desv_gen_vs_epd: desv(gen, epd),
        desv_one_vs_epd: desv(one, epd),
        desv_gen_vs_one: desv(gen, one),
        n_rellenos: [gen, one, epd].filter(v => v != null).length,
        fuente_real: r.epd_fuente_real,
      }
    })
    .filter(f => f.n_rellenos >= 2)

  if (filas.length === 0) return null

  const pct = (v) => v == null ? '—' :
    (v > 0 ? '+' : '') + v.toFixed(0) + ' %'

  return (
    <div style={{ marginTop: '1rem', padding: '0.75rem 0.9rem',
                  background: C.cream, border: `1px solid ${C.border}`,
                  borderRadius: '5px', fontSize: '0.75rem', color: C.dark,
                  lineHeight: '1.55' }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: C.dark, marginBottom: '0.4rem' }}>
        Por qué difieren los 3 escenarios
      </div>
      <p style={{ margin: '0 0 0.5rem' }}>
        Los 3 valores son <strong>todos correctos técnicamente</strong> · reflejan tres formas legítimas de estimar la huella:
      </p>
      <ul style={{ margin: '0 0 0.5rem', paddingLeft: '1.2rem' }}>
        <li><strong style={{ color: '#8B6914' }}>Genérico BD pública</strong> · valor promedio del catálogo (ÖKOBAUDAT / Ecoinvent / IBU) para la tecnología. Refleja datasets europeos genéricos del sector.</li>
        <li><strong style={{ color: '#4A5A10' }}>OneClick LCA</strong> · resultado de introducir la tecnología en OneClick con el dataset propio de la herramienta (típicamente ÖKOBAUDAT + Ecoinvent). Es el valor que se aceptaría en un ACV formal para MAT 1.</li>
        <li><strong style={{ color: C.ok }}>EPD real proyecto</strong> · huella verificada de una EPD publicada por el fabricante (subida en la app o encontrada en las BBDD públicas). Es la más precisa cuando existe.</li>
      </ul>

      {/* Tabla dinámica de desviaciones REALES observadas en este estudio */}
      <div style={{ margin: '0.6rem 0 0.5rem' }}>
        <strong>Desviaciones observadas en este estudio:</strong>
        <div style={{ overflowX: 'auto', marginTop: '0.3rem' }}>
          <table style={{ width: '100%', fontSize: '0.72rem', borderCollapse: 'collapse',
                          background: '#fff', border: `1px solid ${C.border}` }}>
            <thead>
              <tr>
                <th style={{ padding: '0.3rem 0.4rem', borderBottom: `1px solid ${C.border}`, textAlign: 'left', color: C.muted, fontWeight: 600 }}>Tecnología</th>
                <th style={{ padding: '0.3rem 0.4rem', borderBottom: `1px solid ${C.border}`, textAlign: 'center', color: C.muted, fontWeight: 600 }}>Genérico vs EPD real</th>
                <th style={{ padding: '0.3rem 0.4rem', borderBottom: `1px solid ${C.border}`, textAlign: 'center', color: C.muted, fontWeight: 600 }}>OneClick vs EPD real</th>
                <th style={{ padding: '0.3rem 0.4rem', borderBottom: `1px solid ${C.border}`, textAlign: 'center', color: C.muted, fontWeight: 600 }}>Genérico vs OneClick</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f, i) => (
                <tr key={i}>
                  <td style={{ padding: '0.3rem 0.4rem', borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ fontWeight: 600 }}>{f.nombre}</div>
                    {f.fuente_real && (
                      <div style={{ fontSize: '0.65rem', color: C.muted, fontStyle: 'italic' }}>
                        EPD: {f.fuente_real}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.3rem 0.4rem', borderBottom: `1px solid ${C.border}`, textAlign: 'center',
                               color: f.desv_gen_vs_epd != null && Math.abs(f.desv_gen_vs_epd) > 50 ? C.coral : C.dark }}>
                    {pct(f.desv_gen_vs_epd)}
                  </td>
                  <td style={{ padding: '0.3rem 0.4rem', borderBottom: `1px solid ${C.border}`, textAlign: 'center',
                               color: f.desv_one_vs_epd != null && Math.abs(f.desv_one_vs_epd) > 50 ? C.coral : C.dark }}>
                    {pct(f.desv_one_vs_epd)}
                  </td>
                  <td style={{ padding: '0.3rem 0.4rem', borderBottom: `1px solid ${C.border}`, textAlign: 'center' }}>
                    {pct(f.desv_gen_vs_one)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: '0.7rem', color: C.muted, marginTop: '0.3rem', fontStyle: 'italic' }}>
          Valores positivos · el primer escenario está por encima del segundo. Negativos · por debajo. Vacío · falta al menos un escenario para hacer la comparación.
        </div>
      </div>

      <p style={{ margin: '0.5rem 0 0.4rem' }}>
        Motivos generales por los que los 3 escenarios difieren (aplican en distinta proporción según tecnología):
      </p>
      <ul style={{ margin: '0 0 0.5rem', paddingLeft: '1.2rem' }}>
        <li><strong>Antigüedad del dataset</strong> · los datasets genéricos y los que usa OneClick internamente suelen tener 4-8 años de retraso frente a EPDs recientes de fabricantes. En sectores con mejora industrial rápida (FV, aerotermia con R32/R290) esto es el motivo principal.</li>
        <li><strong>Genérico vs específico</strong> · un dataset genérico agrega el promedio del sector, una EPD verificada declara los datos primarios de una fábrica concreta.</li>
        <li><strong>Versión de la norma EN 15804 A1 vs A2</strong> · A2 (2019) sube ligeramente el GWP frente a A1 (2013) por factor CH₄ fósil, asignación económica y 13 indicadores obligatorios.</li>
        <li><strong>Alcance del ciclo declarado</strong> · algunas EPDs son cradle-to-gate (solo A1-A3), otras cradle-to-grave. Los valores comparados aquí son todos A1-A3 (producto) para homogeneidad.</li>
      </ul>
    </div>
  )
}

// ─── Modal · Fuente ACV (Fase 2.7b) ─────────────────────────────────────────
// 3 tabs con las 3 opciones que definimos con Gisell:
//   1. Subir DAP · el asesor tiene la EPD del fabricante concreto del proyecto
//   2. Buscar BBDD · consulta cross-BD via Edge Function epd-search (reusada de MAT1)
//   3. Sin EPD · nota justificativa cuando no hay dato disponible
const CATEGORIAS_LISTA = [
  { codigo: 'GWP-total',      nombre: 'Cambio climático (total)',        unidad: 'kg CO2 eq' },
  { codigo: 'GWP-fossil',     nombre: 'Cambio climático (fósiles)',      unidad: 'kg CO2 eq' },
  { codigo: 'GWP-biogenic',   nombre: 'Cambio climático (biogénico)',    unidad: 'kg CO2 eq' },
  { codigo: 'GWP-luluc',      nombre: 'Cambio climático (usos suelo)',   unidad: 'kg CO2 eq' },
  { codigo: 'ODP',            nombre: 'Agotamiento capa ozono',          unidad: 'kg CFC-11 eq' },
  { codigo: 'AP',             nombre: 'Acidificación',                   unidad: 'mol H+ eq' },
  { codigo: 'EP-freshwater',  nombre: 'Eutrofización (agua dulce)',      unidad: 'kg P eq' },
  { codigo: 'EP-marine',      nombre: 'Eutrofización (marina)',          unidad: 'kg N eq' },
  { codigo: 'EP-terrestrial', nombre: 'Eutrofización (terrestre)',       unidad: 'mol N eq' },
  { codigo: 'POCP',           nombre: 'Formación ozono fotoquímico',     unidad: 'kg NMVOC eq' },
  { codigo: 'ADP-e',          nombre: 'Agotamiento recursos (metales)',  unidad: 'kg Sb eq' },
  { codigo: 'ADP-f',          nombre: 'Agotamiento recursos (fósiles)',  unidad: 'MJ, poder calorífico neto' },
  { codigo: 'WDP',            nombre: 'Uso de agua',                     unidad: 'm3 water eq deprived' },
]
const FASES_LISTA = ['A1-A3', 'A1', 'A2', 'A3', 'A4', 'A5',
                     'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7',
                     'C1', 'C2', 'C3', 'C4', 'D']

function EpdFuenteModal({ study, tecnologia, flash, onClose, onSaved }) {
  const [tab, setTab] = useState('upload')
  // Estado local para mostrar mensajes DENTRO del modal
  // (el banner global del wizard queda tapado por el overlay del modal)
  const [modalMsg, setModalMsg] = useState(null)
  const modalFlash = useCallback((msg, kind = 'info') => {
    setModalMsg({ msg, kind })
    // También lanzamos el flash global por si el modal se cierra
    if (flash) flash(msg, kind)
    setTimeout(() => setModalMsg(null), 5000)
  }, [flash])

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(59,47,47,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '1rem',
  }
  const modalStyle = {
    background: C.card, borderRadius: '8px', maxWidth: '780px', width: '100%',
    maxHeight: '90vh', overflow: 'auto', boxShadow: '0 6px 24px rgba(0,0,0,0.2)',
  }
  const headerStyle = {
    padding: '1rem 1.25rem', borderBottom: `1px solid ${C.border}`,
    display: 'flex', alignItems: 'center', gap: '0.75rem',
  }
  const tabBar = {
    display: 'flex', gap: '0', borderBottom: `1px solid ${C.border}`,
    background: C.cream,
  }
  const tabBtn = (active) => ({
    flex: 1, padding: '0.7rem 0.9rem', background: active ? C.card : 'transparent',
    border: 'none', borderBottom: active ? `2px solid ${C.olive}` : '2px solid transparent',
    fontSize: '0.82rem', fontWeight: active ? 700 : 500,
    color: active ? C.dark : C.muted, cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  })

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={headerStyle}>
          <Files size={20} weight="regular" style={{ color: C.olive }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.7rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Fuente ACV
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: C.dark }}>
              {tecnologia.tech_nombre}
            </div>
          </div>
          <button onClick={onClose}
                  style={{ background: 'transparent', border: 'none', fontSize: '1.4rem',
                           color: C.muted, cursor: 'pointer', padding: '0.2rem 0.5rem' }}>
            ✕
          </button>
        </div>
        <div style={tabBar}>
          <button style={tabBtn(tab === 'upload')} onClick={() => setTab('upload')}>
            1. Subir DAP del proyecto
          </button>
          <button style={tabBtn(tab === 'search')} onClick={() => setTab('search')}>
            2. Buscar en 5 BBDD públicas
          </button>
          <button style={tabBtn(tab === 'note')} onClick={() => setTab('note')}>
            3. Sin EPD · nota
          </button>
        </div>
        <div style={{ padding: '1.25rem' }}>
          {/* Banner de mensajes DENTRO del modal · imprescindible porque el
              overlay tapa el banner global del wizard */}
          {modalMsg && (
            <div style={{
              padding: '0.6rem 0.85rem', marginBottom: '0.85rem',
              background: modalMsg.kind === 'err' ? '#FDF5F3'
                : modalMsg.kind === 'ok' ? '#F1F5E8' : '#FAF7F3',
              border: `1px solid ${modalMsg.kind === 'err' ? C.coral
                : modalMsg.kind === 'ok' ? C.ok : C.border}`,
              borderRadius: '5px', color: C.dark, fontSize: '0.85rem',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              {modalMsg.kind === 'err'
                ? <Warning size={18} weight="regular" />
                : modalMsg.kind === 'ok'
                  ? <CheckCircle size={18} weight="regular" style={{ color: C.ok }} />
                  : <Info size={18} weight="regular" />}
              <span>{modalMsg.msg}</span>
            </div>
          )}
          {tab === 'upload' && (
            <EpdTabUpload study={study} tecnologia={tecnologia}
                          flash={modalFlash} onSaved={onSaved} />
          )}
          {tab === 'search' && (
            <EpdTabSearch study={study} tecnologia={tecnologia}
                          flash={modalFlash} onSaved={onSaved} />
          )}
          {tab === 'note' && (
            <EpdTabNote study={study} tecnologia={tecnologia}
                        flash={modalFlash} onSaved={onSaved} />
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Tab 1 · Subir DAP · file upload + form metadata + tabla de valores ─────
function EpdTabUpload({ study, tecnologia, flash, onSaved }) {
  const [meta, setMeta] = useState({
    fabricante: '', modelo: '', registro_numero: '',
    norma: 'EN 15804+A2:2019', ano_emision: new Date().getFullYear(),
    vida_util: 15,
  })
  const [file, setFile] = useState(null)
  // Valores mínimos por defecto: GWP-total en A1-A3, B6 y D
  const [valores, setValores] = useState([
    { fase: 'A1-A3', categoria: 'GWP-total', valor: '', unidad_categoria: 'kg CO2 eq', unidad_funcional: 'kW' },
    { fase: 'B6',    categoria: 'GWP-total', valor: '', unidad_categoria: 'kg CO2 eq', unidad_funcional: 'kW' },
    { fase: 'D',     categoria: 'GWP-total', valor: '', unidad_categoria: 'kg CO2 eq', unidad_funcional: 'kW' },
  ])
  const [saving, setSaving] = useState(false)
  const [extracting, setExtracting] = useState(false)
  const [autoExtracted, setAutoExtracted] = useState(null)   // metadata de la extracción · {confianza, notas, potencia_declarada_kw}

  // Extracción automática con Claude API (Fase 2.7c)
  // Al pulsar el botón, envía el PDF a la Edge Function `epd-extract`
  // y rellena los campos del formulario con lo que Claude devuelva.
  // El asesor revisa antes de guardar (precisión ~85-90%).
  const extraerDelPdf = async () => {
    if (!file) { flash('Sube un PDF primero', 'err'); return }
    setExtracting(true)
    try {
      // Convertir File a base64 (sin el prefijo data:...)
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const dataUrl = reader.result
          const comma = dataUrl.indexOf(',')
          resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl)
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })

      const { data, error } = await supabase.functions.invoke('epd-extract', {
        body: { pdfBase64: b64, fileName: file.name },
      })
      if (error) throw error
      if (!data?.ok) throw new Error(data?.error || 'Extracción fallida')

      const ex = data.extracted
      // Rellenar metadata (solo campos que Claude ha extraído · null se ignora)
      setMeta(prev => ({
        fabricante:      ex.fabricante      ?? prev.fabricante,
        modelo:          ex.modelo          ?? prev.modelo,
        registro_numero: ex.registro_numero ?? prev.registro_numero,
        norma:           ex.norma           ?? prev.norma,
        ano_emision:     ex.ano_emision     ?? prev.ano_emision,
        vida_util:       ex.vida_util_anos  ?? prev.vida_util,
      }))

      // Rellenar valores GWP · si la EPD declara por unidad y sabemos la
      // potencia declarada, dividimos para tener kg CO2/kW.
      // Si no sabemos potencia, dejamos el valor tal cual y el asesor decide.
      const potKw = Number(ex.potencia_declarada_kw) || null
      const dividir = (v) => (v != null && potKw ? (Number(v) / potKw) : (v ?? ''))
      const nuevosValores = []
      const parejas = [
        ['A1-A3', ex.gwp_total_a1_a3],
        ['A4',    ex.gwp_total_a4],
        ['A5',    ex.gwp_total_a5],
        ['B1',    ex.gwp_total_b1],
        ['B2',    ex.gwp_total_b2],
        ['B6',    ex.gwp_total_b6],
        ['C2',    ex.gwp_total_c2],
        ['C3',    ex.gwp_total_c3],
        ['C4',    ex.gwp_total_c4],
        ['D',     ex.gwp_total_d],
      ]
      for (const [fase, valorPorUnidad] of parejas) {
        if (valorPorUnidad != null) {
          nuevosValores.push({
            fase, categoria: 'GWP-total',
            valor: String(dividir(valorPorUnidad)),
            unidad_categoria: 'kg CO2 eq',
            unidad_funcional: potKw ? 'kW' : 'unidad',
          })
        }
      }
      if (nuevosValores.length > 0) setValores(nuevosValores)

      setAutoExtracted({
        confianza: ex.confianza || 'media',
        notas: ex.notas_extraccion || null,
        potencia_declarada_kw: potKw,
        unidad_declarada: ex.unidad_declarada || null,
        elapsed_ms: data.meta?.elapsed_ms,
      })
      flash(`Extraído en ${((data.meta?.elapsed_ms || 0) / 1000).toFixed(1)}s · confianza ${ex.confianza || 'media'}`, 'ok')
    } catch (e) {
      flash(`Error extrayendo del PDF: ${e.message}`, 'err')
    } finally {
      setExtracting(false)
    }
  }

  const addFila = () => setValores([...valores, {
    fase: 'A1-A3', categoria: 'GWP-total', valor: '', unidad_categoria: 'kg CO2 eq', unidad_funcional: 'kW',
  }])
  const rmFila = (idx) => setValores(valores.filter((_, i) => i !== idx))
  const setFila = (idx, key, val) => {
    const next = [...valores]
    next[idx] = { ...next[idx], [key]: val }
    // Autocompletar unidad al cambiar categoría
    if (key === 'categoria') {
      const cat = CATEGORIAS_LISTA.find(c => c.codigo === val)
      if (cat) next[idx].unidad_categoria = cat.unidad
    }
    setValores(next)
  }

  const guardar = async () => {
    if (!meta.fabricante) { flash('Falta el fabricante', 'err'); return }
    setSaving(true)
    try {
      // 1. Upload PDF a Storage (si hay fichero)
      let storagePath = null
      let storageSize = null
      let storageMime = null
      if (file) {
        const ts = Date.now()
        const safeTechKey = tecnologia.tech_key || 'tec'
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
        storagePath = `${study.id}/${safeTechKey}-${ts}-${safeName}`
        storageSize = file.size
        storageMime = file.type
        const { error: upErr } = await supabase.storage
          .from('boc-epd-uploads').upload(storagePath, file, {
            contentType: file.type, upsert: false,
          })
        if (upErr) throw upErr
      }
      // 2. Insert en boc_epd_uploads
      const { data: { user } } = await supabase.auth.getUser()
      const { data: upload, error: insErr } = await supabase.from('boc_epd_uploads')
        .insert({
          study_id: study.id,
          technology_id: tecnologia.technology_id,
          fabricante: meta.fabricante,
          modelo: meta.modelo || null,
          registro_numero: meta.registro_numero || null,
          norma: meta.norma,
          ano_emision: meta.ano_emision || null,
          vida_util_declarada_anos: meta.vida_util || null,
          storage_path: storagePath || `no-file/${Date.now()}`,
          storage_size_bytes: storageSize,
          storage_mime: storageMime,
          origen: file ? 'subida_manual' : 'pegado_manual',
          user_id: user.id,
        }).select().single()
      if (insErr) throw insErr
      // 3. RPC · insertar factores
      const valoresValidos = valores
        .filter(v => v.valor !== '' && !isNaN(Number(v.valor)))
        .map(v => ({ ...v, valor: Number(v.valor) }))
      if (valoresValidos.length > 0) {
        const { error: rpcErr } = await supabase.rpc('boc_epd_upload_insertar_factores', {
          p_upload_id: upload.id, p_valores: valoresValidos,
        })
        if (rpcErr) throw rpcErr
      }
      flash(`EPD guardada · ${valoresValidos.length} valores importados`, 'ok')
      onSaved()
    } catch (e) {
      flash(`Error al guardar: ${e.message}`, 'err')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      <div style={{ fontSize: '0.8rem', color: C.muted, lineHeight: 1.5 }}>
        Sube la EPD del fabricante concreto instalado en el proyecto (jerarquía 1 · máxima
        prioridad). Rellena la metadata y los valores mínimos que tengas · los que dejes en
        blanco no se cargan.
      </div>

      {/* Metadata */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
        <div><label style={LABEL}>Fabricante *</label>
          <input style={INP} value={meta.fabricante}
                 onChange={e => setMeta({ ...meta, fabricante: e.target.value })}
                 placeholder="Ej. Daikin, JA Solar, Vaillant" /></div>
        <div><label style={LABEL}>Modelo</label>
          <input style={INP} value={meta.modelo}
                 onChange={e => setMeta({ ...meta, modelo: e.target.value })}
                 placeholder="Ej. VRV IV-i 20 kW · JAM72D30/MB" /></div>
        <div><label style={LABEL}>Nº registro EPD</label>
          <input style={INP} value={meta.registro_numero}
                 onChange={e => setMeta({ ...meta, registro_numero: e.target.value })}
                 placeholder="Ej. NEPD-4909-4258-EN" /></div>
        <div><label style={LABEL}>Norma</label>
          <select style={INP} value={meta.norma}
                  onChange={e => setMeta({ ...meta, norma: e.target.value })}>
            <option>EN 15804+A2:2019</option>
            <option>EN 15804+A1:2013</option>
            <option>ISO 14025</option>
            <option>otras</option>
          </select></div>
        <div><label style={LABEL}>Año emisión</label>
          <input style={INP} type="number" value={meta.ano_emision}
                 onChange={e => setMeta({ ...meta, ano_emision: Number(e.target.value) })} /></div>
        <div><label style={LABEL}>Vida útil declarada (años)</label>
          <input style={INP} type="number" value={meta.vida_util}
                 onChange={e => setMeta({ ...meta, vida_util: Number(e.target.value) })} /></div>
      </div>

      {/* File input + botón extracción automática */}
      <div>
        <label style={LABEL}>Archivo PDF de la EPD (opcional)</label>
        <input type="file" accept="application/pdf"
               onChange={e => { setFile(e.target.files?.[0] || null); setAutoExtracted(null) }}
               style={{ ...INP, padding: '0.4rem' }} />
        {file && <div style={{ fontSize: '0.72rem', color: C.ok, marginTop: '0.3rem' }}>
          ✓ {file.name} · {(file.size / 1024).toFixed(1)} KB
        </div>}

        {file && (
          <div style={{ marginTop: '0.6rem', padding: '0.6rem 0.75rem',
                        background: '#F0EDE0', border: `1px solid ${C.border}`,
                        borderRadius: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 600, color: C.dark }}>
                  Rellenar automáticamente desde el PDF
                </div>
                <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: '0.1rem' }}>
                  Extrae fabricante, modelo, número EPD y valores GWP con Claude · precisión ~85-90% · revisa antes de guardar
                </div>
              </div>
              <button style={BTN_P} onClick={extraerDelPdf} disabled={extracting || saving}>
                {extracting
                  ? <><Spinner size={14} weight="regular" /> Analizando PDF…</>
                  : <>🪄 Extraer datos del PDF</>}
              </button>
            </div>
            {autoExtracted && (
              <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.6rem',
                            background: autoExtracted.confianza === 'alta' ? '#E0F0DC'
                              : autoExtracted.confianza === 'media' ? '#FFF5E0' : '#F8E4E0',
                            border: `1px solid ${autoExtracted.confianza === 'alta' ? C.ok
                              : autoExtracted.confianza === 'media' ? C.amber : C.coral}`,
                            borderRadius: '3px', fontSize: '0.72rem', color: C.dark }}>
                <strong>Confianza: {autoExtracted.confianza}</strong>
                {autoExtracted.unidad_declarada && (
                  <> · Unidad declarada: <em>{autoExtracted.unidad_declarada}</em></>
                )}
                {autoExtracted.potencia_declarada_kw && (
                  <> · Potencia: {autoExtracted.potencia_declarada_kw} kW · valores convertidos a kg/kW</>
                )}
                {!autoExtracted.potencia_declarada_kw && (
                  <> · Sin potencia detectada · valores dejados por unidad · ajusta manualmente si aplica</>
                )}
                {autoExtracted.notas && (
                  <div style={{ marginTop: '0.25rem', fontStyle: 'italic', color: C.muted }}>
                    {autoExtracted.notas}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Valores ACV */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
          <label style={{ ...LABEL, marginBottom: 0 }}>Valores ACV mínimos</label>
          <span style={{ fontSize: '0.7rem', color: C.muted }}>
            (deja en blanco los que no tengas)
          </span>
          <button style={{ ...BTN_G, marginLeft: 'auto', padding: '0.2rem 0.5rem' }}
                  onClick={addFila}>+ Añadir fila</button>
        </div>
        <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Fase</th>
              <th style={thStyle}>Categoría</th>
              <th style={thStyle}>Valor</th>
              <th style={thStyle}>Unidad</th>
              <th style={thStyle}>Por</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {valores.map((v, idx) => (
              <tr key={idx}>
                <td style={{ ...tdStyle, padding: '0.2rem' }}>
                  <select style={{ ...INP, padding: '0.25rem 0.4rem' }}
                          value={v.fase} onChange={e => setFila(idx, 'fase', e.target.value)}>
                    {FASES_LISTA.map(f => <option key={f}>{f}</option>)}
                  </select>
                </td>
                <td style={{ ...tdStyle, padding: '0.2rem' }}>
                  <select style={{ ...INP, padding: '0.25rem 0.4rem' }}
                          value={v.categoria} onChange={e => setFila(idx, 'categoria', e.target.value)}>
                    {CATEGORIAS_LISTA.map(c => <option key={c.codigo} value={c.codigo}>{c.nombre}</option>)}
                  </select>
                </td>
                <td style={{ ...tdStyle, padding: '0.2rem' }}>
                  <input style={{ ...INP, padding: '0.25rem 0.4rem', textAlign: 'right' }}
                         type="number" step="any" value={v.valor}
                         onChange={e => setFila(idx, 'valor', e.target.value)}
                         placeholder="0" />
                </td>
                <td style={{ ...tdStyle, fontSize: '0.7rem', color: C.muted }}>
                  {v.unidad_categoria}
                </td>
                <td style={{ ...tdStyle, padding: '0.2rem' }}>
                  <select style={{ ...INP, padding: '0.25rem 0.4rem' }}
                          value={v.unidad_funcional} onChange={e => setFila(idx, 'unidad_funcional', e.target.value)}>
                    <option value="kW">kW</option>
                    <option value="kWp">kWp</option>
                    <option value="m2">m²</option>
                    <option value="unidad">unidad</option>
                  </select>
                </td>
                <td style={{ ...tdStyle, padding: '0.2rem' }}>
                  <button onClick={() => rmFila(idx)}
                          style={{ background: 'transparent', border: 'none',
                                   color: C.coral, cursor: 'pointer', fontSize: '0.9rem' }}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button style={BTN_P} onClick={guardar} disabled={saving}>
          {saving ? <Spinner size={14} weight="regular" /> : <FloppyDisk size={14} weight="regular" />}
          Guardar EPD
        </button>
      </div>
    </div>
  )
}

// ─── Tab 2 · Buscar en las 5 BBDD · reusa Edge Function epd-search de MAT1 ──
function EpdTabSearch({ study, tecnologia, flash, onSaved }) {
  const [query, setQuery] = useState(tecnologia.tech_nombre || '')
  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState([])
  const [saving, setSaving] = useState(false)

  const buscar = async () => {
    if (!query.trim()) return
    setSearching(true)
    setResults([])
    try {
      const { data, error } = await supabase.functions.invoke('epd-search',
        { body: { action: 'search', query: query.trim() } })
      if (error) throw error
      setResults(data?.results || data || [])
    } catch (e) {
      flash(`Error de búsqueda: ${e.message}`, 'err')
    } finally {
      setSearching(false)
    }
  }

  const importar = async (result) => {
    setSaving(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      // Mapeo del nodo epd-search a fuente_bd
      const nodeToBd = {
        oekobaudat: 'ÖKOBAUDAT', ibu: 'IBU', dapcons: 'DAPcons',
        opendap: 'OpenDAP', environdec: 'Environdec',
      }
      const fuente_bd = nodeToBd[result.node] || 'otras'
      const { data: upload, error: insErr } = await supabase.from('boc_epd_uploads')
        .insert({
          study_id: study.id,
          technology_id: tecnologia.technology_id,
          fabricante: result.manufacturer || result.name?.split(' ')[0] || 'Genérico',
          modelo: result.name || null,
          registro_numero: result.uuid || null,
          norma: 'EN 15804+A2:2019',
          ano_emision: result.refYear ? Number(result.refYear) : null,
          storage_path: `epd-search/${result.node}/${result.uuid || Date.now()}`,
          origen: 'buscada_bd_publica',
          fuente_bd,
          epd_search_uuid: result.uuid,
          epd_search_node: result.node,
          user_id: user.id,
        }).select().single()
      if (insErr) throw insErr
      // Intentar extraer GWP si el resultado lo tiene
      const valores = []
      const gwp = result.gwp || result.indicators?.GWP || result.GWP
      if (gwp && !isNaN(Number(gwp))) {
        valores.push({
          fase: 'A1-A3', categoria: 'GWP-total',
          valor: Number(gwp), unidad_categoria: 'kg CO2 eq',
          unidad_funcional: 'kW',
        })
      }
      if (valores.length > 0) {
        const { error: rpcErr } = await supabase.rpc('boc_epd_upload_insertar_factores', {
          p_upload_id: upload.id, p_valores: valores,
        })
        if (rpcErr) throw rpcErr
      }
      flash(`EPD "${result.name}" importada de ${fuente_bd}`, 'ok')
      onSaved()
    } catch (e) {
      flash(`Error al importar: ${e.message}`, 'err')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      <div style={{ fontSize: '0.8rem', color: C.muted, lineHeight: 1.5 }}>
        Busca en las 5 BBDD europeas EPD que la app tiene integradas (jerarquía 2-4 según BD).
        Al importar una EPD, sus valores se cargan en el ACV del estudio automáticamente.
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input style={INP} value={query}
               onChange={e => setQuery(e.target.value)}
               onKeyDown={e => e.key === 'Enter' && buscar()}
               placeholder="Ej. heat pump, photovoltaic, aerotermia..." />
        <button style={BTN_P} onClick={buscar} disabled={searching}>
          {searching ? <Spinner size={14} weight="regular" /> : 'Buscar'}
        </button>
      </div>
      {results.length === 0 && !searching && (
        <div style={{ padding: '1rem', textAlign: 'center', color: C.muted, fontSize: '0.82rem' }}>
          Escribe el nombre de la tecnología y pulsa Buscar
        </div>
      )}
      {results.map((r, idx) => (
        <div key={r.uuid || idx}
             style={{ border: `1px solid ${C.border}`, borderRadius: '4px',
                      padding: '0.7rem 0.85rem', background: C.cream,
                      display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: C.dark }}>
              {r.name}
            </div>
            <div style={{ fontSize: '0.72rem', color: C.muted,
                          fontFamily: "'IBM Plex Mono', monospace", marginTop: '0.15rem' }}>
              {r.nodeLabel || r.node} · {r.geo || '—'} · {r.refYear || '—'}
            </div>
            {r.gwp && (
              <div style={{ fontSize: '0.72rem', color: C.olive, marginTop: '0.15rem' }}>
                GWP A1-A3: {r.gwp} kg CO₂ eq
              </div>
            )}
          </div>
          <button style={BTN_P} onClick={() => importar(r)} disabled={saving}>
            Importar
          </button>
        </div>
      ))}
    </div>
  )
}

// ─── Tab 3 · Sin EPD · nota justificativa ──────────────────────────────────
function EpdTabNote({ study, tecnologia, flash, onSaved }) {
  const [nota, setNota] = useState('')
  const [saving, setSaving] = useState(false)

  const guardar = async () => {
    if (!nota.trim()) { flash('Escribe la justificación', 'err'); return }
    setSaving(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { error } = await supabase.from('boc_epd_uploads')
        .insert({
          study_id: study.id,
          technology_id: tecnologia.technology_id,
          fabricante: 'Sin EPD',
          storage_path: `no-epd/${study.id}/${tecnologia.tech_key}-${Date.now()}`,
          origen: 'pegado_manual',
          user_id: user.id,
          notas_asesor: nota.trim(),
        })
      if (error) throw error
      flash('Justificación guardada · sin EPD para esta tecnología', 'ok')
      onSaved()
    } catch (e) {
      flash(`Error al guardar: ${e.message}`, 'err')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      <div style={{ fontSize: '0.8rem', color: C.muted, lineHeight: 1.5 }}>
        Deja constancia de por qué no hay EPD disponible para esta tecnología. Esta nota se
        incluirá en el docx del estudio como justificación de la ausencia de datos verificados
        para esta tecnología concreta.
      </div>
      <textarea
        value={nota} onChange={e => setNota(e.target.value)}
        placeholder="Ej. El fabricante Daikin no publica EPD verificada para sus equipos VRV en 2024. Se solicitó formalmente y no se obtuvo respuesta. Se usa como referencia la EPD Vaillant aroTHERM plus (misma tecnología aire-agua monobloc)."
        style={{ ...INP, minHeight: '120px', resize: 'vertical', fontFamily: 'inherit',
                 lineHeight: 1.5, padding: '0.6rem' }} />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button style={BTN_P} onClick={guardar} disabled={saving}>
          {saving ? <Spinner size={14} weight="regular" /> : <FloppyDisk size={14} weight="regular" />}
          Guardar justificación
        </button>
      </div>
    </div>
  )
}

const thStyle = {
  padding: '0.5rem 0.4rem', fontSize: '0.72rem', color: C.muted,
  textAlign: 'center', borderBottom: `1px solid ${C.border}`, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.02em',
}
const tdStyle = {
  padding: '0.4rem', fontSize: '0.82rem', color: C.dark, textAlign: 'center',
  borderBottom: `1px solid ${C.border}`, verticalAlign: 'middle',
}
